import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CmsLayout from "@layouts/CmsLayout";
import { useAuth } from "@context/AuthContext";
import MapOverlayPanel from "@components/maps/MapOverlayPanel";
import MapStyleOverlayPanel from "@components/maps/MapStyleOverlayPanel";
import ModeRouteToolPanel from "@components/mode_tool";
import MapBoundariesPanel from "@components/map_boundaries";
import StartPointToolPanel from "@components/start_point";
import BeforeYouGoPanel from "@components/before_you_go";
import MaximizeYourExperiencePanel from "@components/maximize_your_experience";
import ReviewsPanel from "@components/reviews";
import env from "@configs/env";
import useLanguages from "@hooks/languages/useLanguages";
import useMapboxDashboard from "@hooks/maps/useMapboxDashboard";
import useMapBoundaries from "@hooks/map_boundaries/useMapBoundaries";
import useModeRouteTool from "@hooks/mode_tool/useModeRouteTool";
import { importMultimediaFromUrlRequest } from "@services/multimedia/multimediaApi";
import {
  createBeforeYouGoRequest,
  listBeforeYouGoByTourRequest,
  normalizeBeforeYouGoSectionFromApi,
} from "@services/beforeYouGo/beforeYouGoApi";
import {
  listMaximizeYourExperienceByTourRequest,
  normalizeMaximizeYourExperienceSectionFromApi,
  syncMaximizeYourExperienceRequest,
} from "@services/maximizeYourExperience/maximizeYourExperienceApi";
import { listStartPointsByTourRequest } from "@services/startPoints/startPointsApi";
import {
  resetDirectiosApiRoutePoint,
  setDirectiosApiRoutePoint,
} from "@store/slices/directiosApiRoutePointSlice";
import {
  resetDirectiosApiRoutePointRaw,
  setDirectiosApiRoutePointRaw,
} from "@store/slices/directiosApiRoutePointRawSlice";
import { setModePointsForStartMode } from "@store/slices/modeToolDraftSlice";
import { setWalkingRoutesSnapshot } from "@store/slices/modeToolDraftSlice";
import { resetTourDraft } from "@store/slices/tourDraftSlice";
import {
  createDrivingTourPointsRequest,
  createRandomTourPointsRequest,
  createWalkingTourPointsRequest,
  deleteWalkingRouteRequest,
  listAllTourPointsByTourRequest,
  listRandomRouteByTourRequest,
  listWalkingRoutesByTourRequest,
  listTourPointsByTourTypeRequest,
  normalizeTourPointFromApi,
  updateDrivingTourPointsRequest,
  updateRandomTourPointsRequest,
  updateWalkingTourPointsRequest,
} from "@services/tourPoints/tourPointsApi";
import {
  listTourBoundariesByTourRequest,
  normalizeBoundaryPointFromApi,
  syncTourBoundariesRequest,
} from "@services/tourBoundaries/tourBoundariesApi";
import { MAP_STYLES } from "@utils/maps/mapStyles";
import {
  buildPayloadLabelCode,
  buildPayloadRouteGroup,
  buildDrivingRouteGroupName,
  extractRouteTypes,
  groupDrivingPointsByRoute,
  normalizeModeType,
  normalizeDrivingRouteGroupKey,
  resolveTourId,
} from "@utils/mode_tool/modeRoute";
import {
  buildStartPointModel,
  syncStartPointModelsFromMap,
} from "@utils/start_point/panelUtils";
import { hasInvalidRoutePoints } from "@utils/mode_tool/validation";
import {
  filterRoutesByType,
  filterRoutesByTypeAndId,
} from "@utils/tours/tourRoutes";
import "@assets/css/pages/dashboard.css";

function DashboardPage() {
  const dispatch = useDispatch();
  const { authorization, user } = useAuth();
  const primaryRole = String(authorization?.primaryRole || user?.role || "")
    .toLowerCase()
    .trim();
  const isAdminRole = primaryRole === "admin";
  const selectedModePointsDraft = useSelector(
    (state) => state.modeToolDraft.selectedModeDraft?.points || [],
  );
  const modeToolDraft = useSelector((state) => state.modeToolDraft || {});
  const selectedTourTypeId = useSelector(
    (state) => state.modeToolDraft.selectedTourTypeId || "",
  );
  const directiosApiRoutePoint = useSelector(
    (state) => state.directiosApiRoutePoint || {},
  );
  const directiosApiRoutePointRaw = useSelector(
    (state) => state.directiosApiRoutePointRaw?.payload || null,
  );
  const draftSelectedModeForMap = useSelector(
    (state) => state.modeToolDraft?.selectedMode || "",
  );
  const draftSelectedWalkingRouteKeyForMap = useSelector(
    (state) => state.modeToolDraft?.selectedWalkingRouteKey || "",
  );
  const [isTourFormOpen, setIsTourFormOpen] = useState(false);
  const [selectedStartTour, setSelectedStartTour] = useState(null);
  const [startPointTourId, setStartPointTourId] = useState("");
  const [startPoints, setStartPoints] = useState([]);
  const [selectedStartPoint, setSelectedStartPoint] = useState(null);
  const [isModeToolOpen, setIsModeToolOpen] = useState(false);
  const [isBeforeYouGoOpen, setIsBeforeYouGoOpen] = useState(false);
  const [selectedBeforeYouGoTour, setSelectedBeforeYouGoTour] = useState(null);
  const [beforeYouGoSections, setBeforeYouGoSections] = useState([]);
  const [loadingBeforeYouGo, setLoadingBeforeYouGo] = useState(false);
  const [isReviewsOpen, setIsReviewsOpen] = useState(false);
  const [selectedReviewsTour, setSelectedReviewsTour] = useState(null);
  const [isMaximizeExperienceOpen, setIsMaximizeExperienceOpen] =
    useState(false);
  const [selectedMaximizeExperienceTour, setSelectedMaximizeExperienceTour] =
    useState(null);
  const [maximizeExperienceSections, setMaximizeExperienceSections] = useState(
    [],
  );
  const [loadingMaximizeExperience, setLoadingMaximizeExperience] =
    useState(false);
  const [accordionResetKey, setAccordionResetKey] = useState(0);
  const [autoExpandStartPointId, setAutoExpandStartPointId] = useState("");
  const [isPickingStartPoints, setIsPickingStartPoints] = useState(false);
  const [isPickingModePoints, setIsPickingModePoints] = useState(false);
  const [loadingStartPoints, setLoadingStartPoints] = useState(false);
  const [pendingDrivingPointId, setPendingDrivingPointId] = useState("");
  const [pendingModeType, setPendingModeType] = useState("driving");
  const [popupRefreshToken, setPopupRefreshToken] = useState(0);
  const [routeReadySignatureByRoute, setRouteReadySignatureByRoute] = useState(
    {},
  );
  const [walkingRouteShapePointsByKey, setWalkingRouteShapePointsByKey] =
    useState({});
  const [isWalkingRouteShapeEditEnabled, setIsWalkingRouteShapeEditEnabled] =
    useState(false);
  const [isWalkingFromTourMode, setIsWalkingFromTourMode] = useState(false);
  const [selectedRandomRouteTypeId, setSelectedRandomRouteTypeId] =
    useState("");
  const [activeModePopupLanguageIso, setActiveModePopupLanguageIso] =
    useState("");
  const [isImportingModeJson, setIsImportingModeJson] = useState(false);
  const [modeJsonImportMessage, setModeJsonImportMessage] = useState("");
  const [modeJsonImportError, setModeJsonImportError] = useState("");
  const autoRouteRequestRef = useRef("");
  const autoRouteBlockedRef = useRef("");
  const hydratedWithoutRouteSignatureByScopeRef = useRef({});
  const activeModeLoadRequestRef = useRef("");
  const hydratingModeLoadRequestRef = useRef("");
  const isModeToolOpenRef = useRef(false);
  const selectedStartPointIdRef = useRef("");
  const {
    isOpen: isMapBoundariesOpen,
    selectedTour: selectedMapBoundariesTour,
    isPickingPoints: isPickingBoundaryPoints,
    setIsPickingPoints: setIsPickingBoundaryPoints,
    togglePickingPoints: toggleBoundaryPickingPoints,
    openFromTour: openMapBoundariesFromTour,
    close: closeMapBoundariesState,
  } = useMapBoundaries();
  const previousPointsCountRef = useRef(0);
  const getPointPopupDataRef = useRef(() => null);
  const onPointPopupSaveRef = useRef(() => {});
  const onPointMarkerClickRef = useRef(() => {});
  const onPointPopupDeleteRef = useRef(() => {});
  const loadedModeContextRef = useRef("");
  const previousWalkingRouteKeyRef = useRef("");

  useEffect(() => {
    isModeToolOpenRef.current = Boolean(isModeToolOpen);
  }, [isModeToolOpen]);

  useEffect(() => {
    selectedStartPointIdRef.current = String(selectedStartPoint?.id || "");
  }, [selectedStartPoint?.id]);

  function isStaleModeLoad(requestKey = "", expectedStartPointId = "") {
    if (!requestKey) return true;
    if (activeModeLoadRequestRef.current !== requestKey) return true;
    if (!isModeToolOpenRef.current) return true;
    return (
      String(selectedStartPointIdRef.current || "") !==
      String(expectedStartPointId || "")
    );
  }

  function resolveRawDirectionsPayload(rawValue) {
    if (!rawValue) return null;
    if (typeof rawValue === "string") {
      try {
        return JSON.parse(rawValue);
      } catch (error) {
        return null;
      }
    }
    if (typeof rawValue === "object") return rawValue;
    return null;
  }

  function resolveRouteFromRaw(rawValue, options = {}) {
    const raw = resolveRawDirectionsPayload(rawValue);
    if (!raw) return null;
    const rawPreferredRouteKey = String(options?.routeKey || "")
      .trim()
      .toUpperCase();
    const preferredRouteKey =
      rawPreferredRouteKey && rawPreferredRouteKey !== "DEFAULT"
        ? normalizeDrivingRouteGroupKey(rawPreferredRouteKey)
        : "";
    if (Array.isArray(raw?.routes) && raw.routes[0]?.geometry)
      return raw.routes[0];
    if (
      raw?.routes &&
      typeof raw.routes === "object" &&
      !Array.isArray(raw.routes)
    ) {
      const normalizedRoutes = Object.entries(raw.routes).filter(
        ([, routeItem]) =>
          routeItem?.geometry &&
          Array.isArray(routeItem?.geometry?.coordinates),
      );
      const preferredRoute =
        preferredRouteKey && preferredRouteKey !== "main"
          ? raw?.routes?.[preferredRouteKey]
          : null;
      const mainRoute = raw?.routes?.main;

      if (preferredRoute?.geometry?.coordinates?.length) {
        return {
          geometry: preferredRoute.geometry,
          distance: Number(preferredRoute?.distance || 0),
          duration: Number(preferredRoute?.duration || 0),
        };
      }

      if (normalizedRoutes.length === 1) {
        const onlyRoute = normalizedRoutes[0][1];
        return {
          geometry: onlyRoute.geometry,
          distance: Number(onlyRoute?.distance || 0),
          duration: Number(onlyRoute?.duration || 0),
        };
      }

      if (normalizedRoutes.length > 1) {
        return {
          geometry: {
            type: "MultiLineString",
            coordinates: normalizedRoutes.map(
              ([, routeItem]) => routeItem.geometry.coordinates,
            ),
          },
          distance: normalizedRoutes.reduce(
            (total, [, routeItem]) => total + Number(routeItem?.distance || 0),
            0,
          ),
          duration: normalizedRoutes.reduce(
            (total, [, routeItem]) => total + Number(routeItem?.duration || 0),
            0,
          ),
        };
      }

      if (mainRoute?.geometry?.coordinates?.length) {
        return {
          geometry: mainRoute.geometry,
          distance: Number(mainRoute?.distance || 0),
          duration: Number(mainRoute?.duration || 0),
        };
      }
    }
    if (raw?.geometry && Array.isArray(raw?.geometry?.coordinates)) {
      return {
        geometry: raw.geometry,
        distance: Number(raw?.distance || 0),
        duration: Number(raw?.duration || 0),
      };
    }
    return null;
  }

  function buildModePointsSignature(pointsList = []) {
    return (Array.isArray(pointsList) ? pointsList : [])
      .slice()
      .sort(
        (a, b) => Number(a?.number_point || 0) - Number(b?.number_point || 0),
      )
      .map((item) =>
        [
          Number(item?.number_point || 0),
          String(item?.latitude ?? ""),
          String(item?.longitude ?? ""),
          normalizeDrivingRouteGroupKey(
            item?.routeGroup ?? item?.route_group ?? "",
            item?.routeGroupName ?? item?.route_group_name ?? "",
            Number(item?.number_point || item?.order || 0) || 0,
          ),
        ].join(":"),
      )
      .join("|");
  }

  function getActiveRouteScopeKey(
    modeValue = selectedMode,
    routeKey = selectedWalkingRouteKey,
  ) {
    return String(modeValue || "") === "walking"
      ? `walking:${String(routeKey || "w1")}`
      : String(modeValue || "default");
  }

  function getActiveRouteShapeKey(
    modeValue = selectedMode,
    routeKey = selectedWalkingRouteKey,
  ) {
    return getActiveRouteScopeKey(
      modeValue,
      String(modeValue || "") === "walking" ? routeKey : "default",
    );
  }

  function getActiveRouteShapePoints(
    modeValue = selectedMode,
    routeKey = selectedWalkingRouteKey,
  ) {
    const shapeKey = getActiveRouteShapeKey(modeValue, routeKey);
    return normalizeRouteShapePointOrders(
      Array.isArray(walkingRouteShapePointsByKey?.[shapeKey])
        ? walkingRouteShapePointsByKey[shapeKey]
        : [],
    );
  }

  function buildWalkingRouteShapePointsSignature(shapePoints = []) {
    return (Array.isArray(shapePoints) ? shapePoints : [])
      .map((point) => {
        const lng = Number(point?.longitude ?? point?.lng);
        const lat = Number(point?.latitude ?? point?.lat);
        return [
          point?.id || "",
          Number(point?.afterPointOrder || 0) || 0,
          Number.isFinite(lng) ? lng.toFixed(7) : "",
          Number.isFinite(lat) ? lat.toFixed(7) : "",
        ].join(":");
      })
      .join("|");
  }

  function buildWalkingRouteSignature(pointsList = [], shapePoints = []) {
    return `${buildModePointsSignature(pointsList)}::shape::${buildWalkingRouteShapePointsSignature(shapePoints)}`;
  }

  function normalizeRouteShapePointsFromApi(shapePoints = []) {
    return normalizeRouteShapePointOrders(
      (Array.isArray(shapePoints) ? shapePoints : []).map((point, index) => {
        const segmentRatio = readRouteShapePointSegmentRatio(point);
        const hasUsableSegmentRatio =
          segmentRatio !== null && segmentRatio > 0 && segmentRatio < 1;
        return {
          id: String(point?.id || `shape-${index + 1}`),
          latitude: Number(point?.latitude ?? point?.lat),
          longitude: Number(point?.longitude ?? point?.lng),
          afterPointOrder:
            Number(point?.afterPointOrder ?? point?.after_point_order) || 1,
          pointId: String(point?.pointId ?? point?.point_id ?? ""),
          labelCode: String(point?.labelCode ?? point?.label_code ?? ""),
          ...(hasUsableSegmentRatio ? { segmentRatio } : {}),
          order: Number(point?.order || index + 1) || index + 1,
        };
      }),
    );
  }

  function getRouteShapePointAfterOrder(point = {}) {
    return Number(point?.afterPointOrder ?? point?.after_point_order) || 1;
  }

  function readRouteShapePointSegmentRatio(point = {}) {
    const rawRatio = point?.segmentRatio ?? point?.segment_ratio;
    if (rawRatio === undefined || rawRatio === null || rawRatio === "") {
      return null;
    }
    const ratio = Number(rawRatio);
    return Number.isFinite(ratio) ? ratio : null;
  }

  function getRouteShapePointSegmentRatio(point = {}) {
    const ratio = readRouteShapePointSegmentRatio(point);
    if (ratio !== null) return ratio;
    const orderFallback = Number(point?.order);
    return Number.isFinite(orderFallback) ? orderFallback : 0;
  }

  function normalizeRouteShapePointOrders(shapePoints = []) {
    const sorted = [...(Array.isArray(shapePoints) ? shapePoints : [])].sort(
      (a, b) => {
        const afterDiff =
          getRouteShapePointAfterOrder(a) - getRouteShapePointAfterOrder(b);
        if (afterDiff) return afterDiff;
        const ratioDiff =
          getRouteShapePointSegmentRatio(a) -
          getRouteShapePointSegmentRatio(b);
        if (ratioDiff) return ratioDiff;
        return Number(a?.order || 0) - Number(b?.order || 0);
      },
    );
    const pointsBySegment = sorted.reduce((acc, point) => {
      const segmentKey = String(getRouteShapePointAfterOrder(point));
      const current = acc.get(segmentKey) || [];
      current.push(point);
      acc.set(segmentKey, current);
      return acc;
    }, new Map());

    return sorted.map((point, index) => {
      const segmentKey = String(getRouteShapePointAfterOrder(point));
      const siblings = pointsBySegment.get(segmentKey) || [];
      const pointRatio = readRouteShapePointSegmentRatio(point);
      const siblingIndex = Math.max(
        0,
        siblings.findIndex((sibling) => sibling === point),
      );
      const fallbackRatio =
        siblings.length > 1
          ? (siblingIndex + 1) / (siblings.length + 1)
          : 0.5;

      return {
        ...point,
        segmentRatio: pointRatio === null ? fallbackRatio : pointRatio,
        order: index + 1,
      };
    });
  }

  function clampRouteShapeSegmentRatioInCurrentOrder(
    shapePoints = [],
    targetPoint = {},
    nextRatio = 0,
    afterPointOrder = 1,
  ) {
    const targetId = String(targetPoint?.id || "");
    const siblings = [...(Array.isArray(shapePoints) ? shapePoints : [])]
      .filter(
        (point) =>
          getRouteShapePointAfterOrder(point) === Number(afterPointOrder),
      )
      .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0));
    const targetIndex = siblings.findIndex(
      (point) => String(point?.id || "") === targetId,
    );
    if (targetIndex < 0) {
      return Math.max(0, Math.min(1, Number(nextRatio) || 0));
    }
    const minRatio =
      targetIndex > 0
        ? Math.min(1, getRouteShapePointSegmentRatio(siblings[targetIndex - 1]))
        : 0;
    const maxRatio =
      targetIndex < siblings.length - 1
        ? Math.max(0, getRouteShapePointSegmentRatio(siblings[targetIndex + 1]))
        : 1;
    const safeRatio = Math.max(0, Math.min(1, Number(nextRatio) || 0));
    if (maxRatio <= minRatio) return safeRatio;
    const edgePadding = 0.000001;
    return Math.max(
      minRatio + edgePadding,
      Math.min(maxRatio - edgePadding, safeRatio),
    );
  }

  function buildRouteSegmentCoordinates(pointsList = []) {
    const coordinates = (Array.isArray(pointsList) ? pointsList : [])
      .map((point) => ({
        order: Number(point?.number_point || point?.order || 0) || 0,
        lng: Number(point?.longitude ?? point?.lng),
        lat: Number(point?.latitude ?? point?.lat),
        id: String(point?.id || ""),
        labelCode: String(point?.labelCode || point?.label_code || ""),
      }))
      .filter(
        (point) =>
          point.order > 0 &&
          Number.isFinite(point.lng) &&
          Number.isFinite(point.lat),
      )
      .sort((a, b) => a.order - b.order);
    return coordinates;
  }

  function calculateRouteSegmentRatio(lngLat = {}, pointsList = [], order = 1) {
    const coordinates = buildRouteSegmentCoordinates(pointsList);
    const segmentIndex = coordinates.findIndex(
      (point) => Number(point.order) === Number(order),
    );
    if (segmentIndex < 0 || segmentIndex >= coordinates.length - 1) return 0;
    const start = coordinates[segmentIndex];
    const end = coordinates[segmentIndex + 1];
    const target = {
      lng: Number(lngLat?.longitude ?? lngLat?.lng),
      lat: Number(lngLat?.latitude ?? lngLat?.lat),
    };
    if (!Number.isFinite(target.lng) || !Number.isFinite(target.lat)) return 0;
    const dx = end.lng - start.lng;
    const dy = end.lat - start.lat;
    const lengthSquared = dx * dx + dy * dy || 1;
    return Math.max(
      0,
      Math.min(
        1,
        ((target.lng - start.lng) * dx + (target.lat - start.lat) * dy) /
          lengthSquared,
      ),
    );
  }

  function calculateNearestRouteSegmentInfo(lngLat = {}, pointsList = []) {
    const coordinates = buildRouteSegmentCoordinates(pointsList);
    if (coordinates.length < 2) {
      return {
        afterPointOrder: coordinates[0]?.order || 1,
        pointId: coordinates[0]?.id || "",
        labelCode: coordinates[0]?.labelCode || "",
        segmentRatio: 0,
      };
    }
    const target = {
      lng: Number(lngLat?.longitude ?? lngLat?.lng),
      lat: Number(lngLat?.latitude ?? lngLat?.lat),
    };
    if (!Number.isFinite(target.lng) || !Number.isFinite(target.lat)) {
      return {
        afterPointOrder: coordinates[0]?.order || 1,
        pointId: coordinates[0]?.id || "",
        labelCode: coordinates[0]?.labelCode || "",
        segmentRatio: 0,
      };
    }
    let nearestInfo = {
      afterPointOrder: coordinates[0].order,
      pointId: coordinates[0]?.id || "",
      labelCode: coordinates[0]?.labelCode || "",
      segmentRatio: 0,
    };
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index < coordinates.length - 1; index += 1) {
      const start = coordinates[index];
      const end = coordinates[index + 1];
      const dx = end.lng - start.lng;
      const dy = end.lat - start.lat;
      const lengthSquared = dx * dx + dy * dy || 1;
      const t = Math.max(
        0,
        Math.min(
          1,
          ((target.lng - start.lng) * dx + (target.lat - start.lat) * dy) /
            lengthSquared,
        ),
      );
      const projectedLng = start.lng + t * dx;
      const projectedLat = start.lat + t * dy;
      const distance =
        (target.lng - projectedLng) * (target.lng - projectedLng) +
        (target.lat - projectedLat) * (target.lat - projectedLat);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestInfo = {
          afterPointOrder: start.order,
          pointId: start?.id || "",
          labelCode: start?.labelCode || "",
          segmentRatio: t,
        };
      }
    }
    return nearestInfo;
  }

  function calculateNearestRouteSegmentOrder(lngLat = {}, pointsList = []) {
    const segmentInfo = calculateNearestRouteSegmentInfo(lngLat, pointsList);
    return Number(segmentInfo?.afterPointOrder || segmentInfo || 1) || 1;
  }
  function insertWalkingShapePoints(pointsList = [], shapePoints = []) {
    const sortedPoints = [
      ...(Array.isArray(pointsList) ? pointsList : []),
    ].sort(
      (a, b) => Number(a?.number_point || 0) - Number(b?.number_point || 0),
    );
    const shapeByAfterOrder = (Array.isArray(shapePoints) ? shapePoints : [])
      .filter((point) => {
        const lng = Number(point?.longitude ?? point?.lng);
        const lat = Number(point?.latitude ?? point?.lat);
        return Number.isFinite(lng) && Number.isFinite(lat);
      })
      .reduce((acc, point) => {
        const afterOrder = Number(point?.afterPointOrder || 0) || 1;
        if (!acc[afterOrder]) acc[afterOrder] = [];
        acc[afterOrder].push(point);
        return acc;
      }, {});
    return sortedPoints.flatMap((point) => {
      const order = Number(point?.number_point || point?.order || 0) || 0;
      const relatedShapePoints = normalizeRouteShapePointOrders(
        shapeByAfterOrder[order] || [],
      );
      return [
        point,
        ...relatedShapePoints.map((shapePoint) => ({
          number_point: order,
          longitude: shapePoint.longitude ?? shapePoint.lng,
          latitude: shapePoint.latitude ?? shapePoint.lat,
          isRouteShapePoint: true,
        })),
      ];
    });
  }

  function isPersistedWalkingRouteId(value) {
    const key = String(value || "");
    return key.includes("-") && key.length >= 16;
  }

  function hasInvalidModeRequiredFields(pointsList = []) {
    return hasInvalidRoutePoints(pointsList);
  }

  function hasEnoughCoordinatesForRoute(pointsList = []) {
    return (
      (Array.isArray(pointsList) ? pointsList : [])
        .map((item) => [Number(item?.longitude), Number(item?.latitude)])
        .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]))
        .length >= 2
    );
  }

  function isCurrentRouteReady(modeValue, pointsList, routeKey = "default") {
    if (modeValue !== "driving" && modeValue !== "walking") return true;
    const scopeKey = getActiveRouteScopeKey(modeValue, routeKey);
    const activeShapePoints = getActiveRouteShapePoints(modeValue, routeKey);
    const currentSignature = activeShapePoints.length
      ? buildWalkingRouteSignature(pointsList, activeShapePoints)
      : buildModePointsSignature(pointsList);
    const readySignature = routeReadySignatureByRoute?.[scopeKey] || "";
    if (readySignature && readySignature === currentSignature) return true;
    return Boolean(
      hasEnoughCoordinatesForRoute(pointsList) &&
      (directiosApiRoutePoint?.geometry ||
        resolveRouteFromRaw(directiosApiRoutePointRaw)?.geometry),
    );
  }

  const resolvedExternalRouteGeometry =
    directiosApiRoutePoint?.geometry ||
    resolveRouteFromRaw(directiosApiRoutePointRaw)?.geometry ||
    null;
  const {
    mapContainerRef,
    selectedStyle,
    setSelectedStyle,
    is3DEnabled,
    setIs3DEnabled,
    isPointMode,
    setIsPointMode,
    points,
    setPointsFromList,
    removePointByIndex,
    removePointById,
    clearAllPoints,
    setIsDrawingEnabled,
    setRouteColor,
    setRouteDashArray,
    isMeasurementEnabled,
    measurementDistanceKm,
    measurementPointCount,
    toggleMeasurement,
    setMarkerVariant,
    clearRouteGeometry,
    forceDrawExternalRoute,
    drawExternalRouteWithRetry,
    drawCombinedRoutesPreview,
    drawBoundaryPolygonFromPoints,
    clearBoundaryPolygon,
    closeActivePointPopup,
    focusPointById,
    focusLocation,
    focusPointsBounds,
    clearLocationMarker,
    syncDrivingMarkerLabelsFromList,
    syncRandomMarkerIconsFromList,
  } = useMapboxDashboard({
    mapboxToken: process.env.REACT_APP_MAPBOX_TOKEN,
    initialStyle: process.env.REACT_APP_MAPBOX_STYLE || MAP_STYLES[0].value,
    // Mode tool no longer draws the follow line between points.
    isRouteEnabled: false,
    externalRouteGeometry: resolvedExternalRouteGeometry,
    isBoundaryAutoDrawEnabled: Boolean(
      isMapBoundariesOpen && isPickingBoundaryPoints,
    ),
    isModePointPopupEnabled: Boolean(isModeToolOpen && selectedStartPoint),
    popupRefreshToken,
    popupLanguageRefreshKey: activeModePopupLanguageIso,
    getPointPopupData: (pointId) => getPointPopupDataRef.current(pointId),
    onPointPopupSave: (pointId, payload) =>
      onPointPopupSaveRef.current(pointId, payload),
    onPointMarkerClick: (point) => onPointMarkerClickRef.current(point),
    onPointPopupDelete: (pointId) => onPointPopupDeleteRef.current(pointId),
    isRouteShapeEditEnabled:
      (draftSelectedModeForMap === "walking" ||
        draftSelectedModeForMap === "driving") &&
      isWalkingRouteShapeEditEnabled,
    routeShapeMode: draftSelectedModeForMap,
    routeShapePoints:
      draftSelectedModeForMap === "walking" ||
      draftSelectedModeForMap === "driving"
        ? getActiveRouteShapePoints(
            draftSelectedModeForMap,
            draftSelectedWalkingRouteKeyForMap,
          )
        : [],
    onAddRouteShapePoint: handleAddWalkingRouteShapePoint,
    onMoveRouteShapePoint: handleMoveWalkingRouteShapePoint,
    onDeleteRouteShapePoint: handleDeleteWalkingRouteShapePoint,
  });

  useEffect(() => {
    // Debounced refresh keeps typing fluid while still updating popup previews.
    const timeoutId = window.setTimeout(() => {
      setPopupRefreshToken((prev) => prev + 1);
    }, 260);
    return () => window.clearTimeout(timeoutId);
  }, [selectedModePointsDraft]);
  const {
    languages,
    loading: languagesLoading,
    error: languagesError,
  } = useLanguages();
  const {
    selectedMode,
    selectedModePointId,
    modeOptions,
    activeModePoints,
    modePointCount,
    selectedWalkingRouteKey,
    activeWalkingRouteKeys,
    activeWalkingRouteMetaByKey,
    selectWalkingRoute,
    createWalkingRoute,
    updateWalkingRouteName,
    removeWalkingRouteLocal,
    suppressNextModePointAutoSelect,
    setSelectedModePointId,
    resetToolState,
    handleSelectStartPoint: selectStartPointForModeTool,
    handleModeSelect,
    getActiveModePointById,
    commitActiveModePoint,
    removeActiveModePoint,
    uploadModePointMediaFiles,
  } = useModeRouteTool({
    isPointMode,
    draftPoints: startPoints,
    mapPoints: points,
    selectedStartPoint,
    setIsDrawingEnabled,
    setRouteColor,
    setRouteDashArray,
    setMarkerVariant,
    setPointsFromList,
    onModePointCreated: () => {
      setIsPickingModePoints(false);
    },
  });
  const hasModePointValidationErrors = hasInvalidModeRequiredFields(
    Array.isArray(selectedModePointsDraft) && selectedModePointsDraft.length
      ? selectedModePointsDraft
      : activeModePoints,
  );

  getPointPopupDataRef.current = (pointId) => {
    const point = getActiveModePointById(pointId) || null;
    if (!point) return null;
    return {
      ...point,
      activeLanguageIso: activeModePopupLanguageIso,
    };
  };
  onPointPopupSaveRef.current = (pointId, payload) => {
    commitActiveModePoint(pointId, payload);
    setSelectedModePointId(pointId);
  };
  onPointMarkerClickRef.current = (point) => {
    if (!point?.id) return;
    setSelectedModePointId(point.id);
  };
  onPointPopupDeleteRef.current = (pointId) => {
    removeActiveModePoint(pointId);
    setSelectedModePointId((prev) => (prev === pointId ? "" : prev));
  };

  useEffect(() => {
    if (selectedMode !== "driving") return;
    syncDrivingMarkerLabelsFromList(activeModePoints || []);
  }, [activeModePoints, selectedMode, syncDrivingMarkerLabelsFromList]);

  useEffect(() => {
    // Keeps random marker icons synchronized with the latest pointType selected in the editor.
    if (selectedMode !== "random") return;
    syncRandomMarkerIconsFromList(activeModePoints || []);
  }, [activeModePoints, selectedMode, syncRandomMarkerIconsFromList]);

  function handleBackFromTourForm() {
    setIsTourFormOpen(false);
    dispatch(resetTourDraft());
    clearLocationMarker();
  }

  async function refreshStartPointsFromBackend(tour = selectedStartTour) {
    // Canonical refresh used after start-point create and when selecting a tour.
    const resolvedTourId = resolveTourId(tour);
    if (!resolvedTourId) return;
    setLoadingStartPoints(true);
    try {
      const fetchedPoints = await listStartPointsByTourRequest(resolvedTourId);
      const normalizedPoints = (
        Array.isArray(fetchedPoints) ? fetchedPoints : []
      ).map((point, index) => buildStartPointModel(point, index));
      setStartPointTourId(resolvedTourId);
      setStartPoints(normalizedPoints);
      setPointsFromList(normalizedPoints, {
        markerColor: "#ef4444",
        markerVariant: "start-point",
      });
    } catch (error) {
      // Keep current draft if refresh fails.
    } finally {
      setLoadingStartPoints(false);
    }
  }

  async function handleSelectTourForStartPoints(payload) {
    const { tour, startPoints, tourId } = payload || {};
    if (!tour) return;
    const resolvedTourId = tourId || resolveTourId(tour);
    if (!resolvedTourId) return;
    clearMapBoundariesArtifacts();
    closeMapBoundariesState();
    setSelectedStartTour(tour);
    setSelectedStartPoint(null);
    setIsPickingStartPoints(false);
    setIsPickingModePoints(false);
    resetToolState();
    clearLocationMarker();
    setIsPointMode(true);
    await refreshStartPointsFromBackend(tour);
  }

  function handleSelectStartPoint(point) {
    setIsWalkingFromTourMode(false);
    setSelectedStartPoint(point || null);
    if (!isModeToolOpen) return;
    setIsPickingModePoints(false);
    setAutoExpandStartPointId("");
  }

  function pickTourDisplayName(tour) {
    const names = tour?.name || tour?.description?.name || {};
    const en =
      typeof names?.en === "string" ? names.en : names?.en?.value || "";
    const es =
      typeof names?.es === "string" ? names.es : names?.es?.value || "";
    const pt =
      typeof names?.pt === "string" ? names.pt : names?.pt?.value || "";
    return en || es || pt || String(tour?.cityName || tour?.id || "");
  }

  function handleSelectTourForWalkingPoints(payload) {
    const { tour, tourId } = payload || {};
    const resolvedTourId = tourId || resolveTourId(tour);
    if (!tour || !resolvedTourId) return;
    const syntheticPoint = {
      id: String(resolvedTourId),
      zoneName: pickTourDisplayName(tour),
      tour_types: {
        id: String(resolvedTourId),
        type: "walking",
      },
    };
    clearMapBoundariesArtifacts();
    closeMapBoundariesState();
    setSelectedStartTour(tour);
    setSelectedStartPoint(syntheticPoint);
    setIsWalkingFromTourMode(true);
    setIsPickingStartPoints(false);
    setIsPickingModePoints(false);
    setIsModeToolOpen(true);
    setAutoExpandStartPointId("");
    setIsPointMode(true);
    selectStartPointForModeTool(syntheticPoint);
    setPendingModeType("walking");
    setPendingDrivingPointId(String(resolvedTourId));
  }

  function handleSelectTourForRandomPoints(payload) {
    const { tour, tourId } = payload || {};
    const resolvedTourId = tourId || resolveTourId(tour);
    if (!tour || !resolvedTourId) return;
    const syntheticPoint = {
      id: String(resolvedTourId),
      zoneName: pickTourDisplayName(tour),
      tour_types: {
        id: String(resolvedTourId),
        type: "random",
      },
    };
    clearMapBoundariesArtifacts();
    closeMapBoundariesState();
    setSelectedStartTour(tour);
    setSelectedStartPoint(syntheticPoint);
    // Reuse this flag for "opened Route Types from tour" flows (walking/random).
    setIsWalkingFromTourMode(true);
    setIsPickingStartPoints(false);
    setIsPickingModePoints(false);
    setIsModeToolOpen(true);
    setAutoExpandStartPointId("");
    setIsPointMode(true);
    setSelectedRandomRouteTypeId("");
    selectStartPointForModeTool(syntheticPoint);
    setPendingModeType("random");
    setPendingDrivingPointId(String(resolvedTourId));
  }

  async function handleSelectTourForBeforeYouGo(payload) {
    const { tour, tourId } = payload || {};
    const resolvedTourId = tourId || resolveTourId(tour);
    if (!tour || !resolvedTourId) return;
    setIsBeforeYouGoOpen(true);
    setSelectedBeforeYouGoTour(tour);
    setBeforeYouGoSections([]);
    setLoadingBeforeYouGo(true);
    try {
      const response = await listBeforeYouGoByTourRequest(resolvedTourId);
      const list = Array.isArray(response?.body?.beforeYouGo)
        ? response.body.beforeYouGo
        : [];
      setBeforeYouGoSections(
        list.map((section) => normalizeBeforeYouGoSectionFromApi(section)),
      );
    } catch (error) {
      setBeforeYouGoSections([]);
    } finally {
      setLoadingBeforeYouGo(false);
    }
  }

  async function handleSelectTourForMaximizeExperience(payload) {
    const { tour, tourId } = payload || {};
    const resolvedTourId = tourId || resolveTourId(tour);
    if (!tour || !resolvedTourId) return;
    setIsBeforeYouGoOpen(false);
    setSelectedBeforeYouGoTour(null);
    setBeforeYouGoSections([]);
    setLoadingBeforeYouGo(false);
    setIsMaximizeExperienceOpen(true);
    setSelectedMaximizeExperienceTour(tour);
    setMaximizeExperienceSections([]);
    setLoadingMaximizeExperience(true);
    try {
      const response =
        await listMaximizeYourExperienceByTourRequest(resolvedTourId);
      const list = Array.isArray(response?.body?.maximazeYourExperience)
        ? response.body.maximazeYourExperience
        : [];
      setMaximizeExperienceSections(
        list.map((section) =>
          normalizeMaximizeYourExperienceSectionFromApi(section),
        ),
      );
    } catch (error) {
      setMaximizeExperienceSections([]);
    } finally {
      setLoadingMaximizeExperience(false);
    }
  }

  function finishHydratingModeLoad(requestKey = "") {
    if (hydratingModeLoadRequestRef.current === requestKey) {
      hydratingModeLoadRequestRef.current = "";
    }
  }

  function handleSelectTourForReviews(payload) {
    const { tour, tourId } = payload || {};
    const resolvedTourId = tourId || resolveTourId(tour);
    if (!tour || !resolvedTourId) return;
    closeActivePointPopup();
    setSelectedStartTour(null);
    setSelectedStartPoint(null);
    setIsPointMode(false);
    setIsModeToolOpen(false);
    setIsPickingStartPoints(false);
    setIsPickingModePoints(false);
    clearLocationMarker();
    dispatch(resetDirectiosApiRoutePoint());
    dispatch(resetDirectiosApiRoutePointRaw());
    forceDrawExternalRoute(null);
    drawExternalRouteWithRetry(null);
    clearRouteGeometry();
    handleClearPreviewRoutesOnMap();
    clearAllPoints();
    clearBoundaryPolygon();
    closeMapBoundariesState();
    setRouteReadySignatureByRoute({});
    setIsBeforeYouGoOpen(false);
    setSelectedBeforeYouGoTour(null);
    setBeforeYouGoSections([]);
    setLoadingBeforeYouGo(false);
    setIsMaximizeExperienceOpen(false);
    setSelectedMaximizeExperienceTour(null);
    setMaximizeExperienceSections([]);
    setLoadingMaximizeExperience(false);
    setIsReviewsOpen(true);
    setSelectedReviewsTour(tour);
  }

  async function handleSelectTourForMapBoundaries(payload) {
    const { tour, tourId } = payload || {};
    const opened = openMapBoundariesFromTour(tour, tourId);
    if (!opened) return;
    const resolvedTourId = resolveTourId(tour);
    setSelectedStartTour(null);
    setSelectedStartPoint(null);
    setIsPointMode(true);
    setIsModeToolOpen(false);
    setIsPickingStartPoints(false);
    setIsPickingModePoints(false);
    setPointsFromList([], {
      markerColor: "#0ea5e9",
      markerVariant: "default",
    });
    forceDrawExternalRoute(null);
    drawExternalRouteWithRetry(null);
    clearRouteGeometry();
    drawBoundaryPolygonFromPoints([]);
    if (!resolvedTourId) return;
    try {
      const response = await listTourBoundariesByTourRequest(resolvedTourId);
      const boundaries = Array.isArray(response?.body?.boundaries)
        ? response.body.boundaries
        : [];
      const normalizedPoints = boundaries
        .map((point) => normalizeBoundaryPointFromApi(point))
        .filter(
          (point) => Number.isFinite(point?.lng) && Number.isFinite(point?.lat),
        )
        .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0));
      setPointsFromList(normalizedPoints, {
        markerColor: "#0ea5e9",
        markerVariant: "default",
      });
      if (normalizedPoints.length) {
        drawBoundaryPolygonFromPoints(normalizedPoints);
        focusPointsBounds(normalizedPoints, {
          padding: 100,
          maxZoom: 12,
          withMarker: false,
        });
      }
    } catch (error) {}
  }

  function normalizePointAudiosForSave(point = {}) {
    const fromAudios = Array.isArray(point?.audios) ? point.audios : [];
    const fromMultimedia = Array.isArray(point?.multimedia)
      ? point.multimedia
          .filter((item) => {
            const type = String(item?.mediaType || "").toLowerCase();
            const mime = String(item?.mimeType || "").toLowerCase();
            return type === "audio" || mime.startsWith("audio/");
          })
          .map((item) => ({
            id: item?.sourceId || item?.id || "",
            url: item?.url || "",
            language: String(item?.language || item?.languageIso || "")
              .toLowerCase()
              .trim(),
          }))
      : [];
    const audioMap = new Map();
    [...fromAudios, ...fromMultimedia].forEach((item) => {
      const language = String(item?.language || item?.languageIso || "")
        .toLowerCase()
        .trim();
      if (!language) return;
      audioMap.set(language, {
        id: item?.id || "",
        url: item?.url || "",
        language,
      });
    });
    return Array.from(audioMap.values());
  }

  function getActiveModeImportRouteKey(modeValue = selectedMode) {
    return String(modeValue || "") === "walking"
      ? String(selectedWalkingRouteKey || "")
      : "default";
  }

  function normalizeImportedRouteGroup(value = "") {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return "main";
    if (/^detour[a-j]$/i.test(normalizedValue)) {
      return String(normalizedValue.slice(-1)).toUpperCase();
    }
    return normalizeDrivingRouteGroupKey(normalizedValue);
  }

  function escapeImportedRichTextHtml(value = "") {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatImportedRichText(value = "") {
    const normalizedText = String(value || "")
      .replace(/\r\n?/g, "\n")
      .trim();
    if (!normalizedText) return "";
    if (/<(?:p|br|strong|em|u|ul|ol|li|h[1-6])\b/i.test(normalizedText)) {
      return normalizedText;
    }

    const formattedLines = normalizedText.split("\n").map((line) => {
      const escapedLine = escapeImportedRichTextHtml(line);
      return escapedLine.replace(
        /(^|[\s>])#([^\s#<]+)/g,
        (_, prefix, content) => `${prefix}<strong>${content}</strong>`,
      );
    });

    const paragraphs = [];
    let currentParagraph = [];

    formattedLines.forEach((line) => {
      if (!line.trim()) {
        if (currentParagraph.length) {
          paragraphs.push(`<p>${currentParagraph.join("<br />")}</p>`);
          currentParagraph = [];
        } else {
          paragraphs.push("<p><br /></p>");
        }
        return;
      }

      currentParagraph.push(line);
    });

    if (currentParagraph.length) {
      paragraphs.push(`<p>${currentParagraph.join("<br />")}</p>`);
    }

    return paragraphs.join("");
  }

  function normalizeImportedLocalizedField(
    value = {},
    languageItems = [],
    formatter = null,
  ) {
    const source = value && typeof value === "object" ? value : {};
    return (Array.isArray(languageItems) ? languageItems : []).reduce(
      (acc, language) => {
        const iso = String(
          language?.lng_iso || language?.iso || "",
        ).toLowerCase();
        if (!iso) return acc;
        acc[iso] = {
          value:
            typeof formatter === "function"
              ? formatter(source?.[iso] ?? "")
              : String(source?.[iso] ?? ""),
          id: "",
        };
        return acc;
      },
      {},
    );
  }

  function readRoutePointExportValue(entry) {
    if (typeof entry === "string") return entry;
    if (!entry || typeof entry !== "object") return "";
    return String(entry?.value ?? entry?.message ?? entry?.text ?? "");
  }

  function buildRoutePointExportTranslations(field = {}) {
    const source = field && typeof field === "object" ? field : {};
    return (Array.isArray(languages) ? languages : []).reduce(
      (translations, language) => {
        const iso = String(language?.lng_iso || language?.iso || "")
          .toLowerCase()
          .trim();
        if (!iso) return translations;
        const localizedEntry = Array.isArray(source)
          ? source.find(
              (entry) =>
                entry &&
                typeof entry === "object" &&
                Object.prototype.hasOwnProperty.call(entry, iso),
            )?.[iso]
          : source?.[iso];
        translations[iso] = readRoutePointExportValue(localizedEntry);
        return translations;
      },
      {},
    );
  }

  function getRoutePointExportMedia(point = {}) {
    const multimedia = Array.isArray(point?.multimedia)
      ? point.multimedia
      : [];
    const images = [
      ...(Array.isArray(point?.images) ? point.images : []),
      ...multimedia.filter((item) => {
        const mediaType = String(item?.mediaType || "").toLowerCase();
        const mimeType = String(item?.mimeType || "").toLowerCase();
        return mediaType === "image" || mimeType.startsWith("image/");
      }),
    ];
    const videos = [
      ...(Array.isArray(point?.videos) ? point.videos : []),
      ...multimedia.filter((item) => {
        const mediaType = String(item?.mediaType || "").toLowerCase();
        const mimeType = String(item?.mimeType || "").toLowerCase();
        return mediaType === "video" || mimeType.startsWith("video/");
      }),
    ];
    const audioUrls = normalizePointAudiosForSave(point).reduce(
      (urls, audio) => {
        const iso = String(audio?.language || "").toLowerCase().trim();
        const url = String(audio?.url || "").trim();
        if (iso && url) urls[iso] = url;
        return urls;
      },
      {},
    );
    const uniqueUrls = (items) =>
      Array.from(
        new Set(
          items
            .map((item) => String(item?.url || item || "").trim())
            .filter(Boolean),
        ),
      );
    return {
      photoUrls: uniqueUrls(images),
      audioUrls,
      videoUrls: uniqueUrls(videos),
    };
  }

  function buildRoutePointExportFileName() {
    const startPointName = readRoutePointExportValue(
      selectedStartPoint?.name?.en,
    );
    const tourNames = selectedStartTour?.name || {};
    const tourName =
      readRoutePointExportValue(tourNames?.en) ||
      readRoutePointExportValue(tourNames) ||
      (tourNames && typeof tourNames === "object"
        ? Object.values(tourNames)
            .map(readRoutePointExportValue)
            .find(Boolean)
        : "") ||
      "";
    const walkingRouteNames =
      activeWalkingRouteMetaByKey?.[selectedWalkingRouteKey]?.name || {};
    const walkingRouteName =
      readRoutePointExportValue(walkingRouteNames?.en) ||
      Object.values(walkingRouteNames)
        .map(readRoutePointExportValue)
        .find(Boolean) ||
      "";
    const exportName =
      selectedMode === "random"
        ? `${tourName || "Tour"}-Point-of-Interest`
        : (selectedMode === "walking" ? walkingRouteName : "") ||
          startPointName ||
          selectedStartPoint?.id ||
          "route";
    const safeStartPointName = String(
      exportName,
    )
      .trim()
      .replace(/[^a-z0-9-_]+/gi, "-")
      .replace(/^-+|-+$/g, "");
    if (selectedMode === "random") {
      return `${safeStartPointName || "Tour-Point-of-Interest"}.json`;
    }
    return `${safeStartPointName || "route"}-${selectedMode || "points"}.json`;
  }

  function handleExportModeJson() {
    setModeJsonImportError("");
    setModeJsonImportMessage("");
    const points = (
      Array.isArray(selectedModePointsDraft) && selectedModePointsDraft.length
        ? selectedModePointsDraft
        : Array.isArray(activeModePoints)
          ? activeModePoints
          : []
    )
      .slice()
      .sort((firstPoint, secondPoint) =>
        Number(firstPoint?.order || 0) - Number(secondPoint?.order || 0),
      );
    if (!selectedMode || !points.length) {
      setModeJsonImportError("There are no Route Type points to download.");
      return;
    }

    const exportPayload = {
      points: points.map((point, index) => ({
        id: String(point?.serverId || point?.uuid || point?.id || ""),
        pointNumber: Number(point?.order || index + 1) || index + 1,
        pointType: String(point?.pointType || ""),
        labelCode: String(point?.labelCode || ""),
        name: buildRoutePointExportTranslations(point?.name),
        description: buildRoutePointExportTranslations(point?.description),
        infoText: buildRoutePointExportTranslations(point?.more_info),
        notes: String(point?.notes || ""),
        triggerRadiusMeters:
          Number(point?.radiusMeters ?? point?.radius_meters) || 0,
        turnType: Number(point?.turnCase ?? point?.turn_case) || 0,
        routeGroup: String(
          point?.routeGroup ?? point?.route_group ?? "main",
        ),
        coordinate: {
          latitude: Number(point?.latitude),
          longitude: Number(point?.longitude),
        },
        media: getRoutePointExportMedia(point),
      })),
    };

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
      type: "application/json;charset=utf-8",
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = buildRoutePointExportFileName();
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
    setModeJsonImportMessage(
      `${points.length} route points downloaded successfully.`,
    );
  }

  function buildImportedMediaFileName(url = "", fallbackBaseName = "media") {
    try {
      const pathname = new URL(String(url || "")).pathname || "";
      const filename = pathname.split("/").pop() || "";
      if (filename) return filename;
    } catch (error) {}
    return `${fallbackBaseName}`;
  }

  function isFirebaseStorageUrl(url = "") {
    return /firebasestorage\.googleapis\.com/i.test(String(url || ""));
  }

  async function downloadImportedMediaAsFile(
    url,
    fallbackBaseName = "media",
    mimeFallback = "",
  ) {
    const response = await fetch(String(url || ""));
    if (!response.ok) {
      throw new Error(`Failed to download media: ${url}`);
    }
    const blob = await response.blob();
    const blobType = String(blob?.type || mimeFallback || "").trim();
    const filename = buildImportedMediaFileName(url, fallbackBaseName);
    return new File([blob], filename, {
      type: blobType || undefined,
    });
  }

  async function uploadImportedMediaUrls(
    urls = [],
    {
      baseName = "media",
      languageIso = "",
      mediaRole = "",
      mimeFallback = "",
    } = {},
  ) {
    const safeUrls = (Array.isArray(urls) ? urls : []).filter(Boolean);
    if (!safeUrls.length) return [];
    const importedFromUrl = [];
    const fallbackUrls = [];
    for (const url of safeUrls) {
      if (!isFirebaseStorageUrl(url)) {
        fallbackUrls.push(url);
        continue;
      }
      try {
        const response = await importMultimediaFromUrlRequest(url);
        const body = response?.body || {};
        const rawItem = Array.isArray(body?.multimediaList)
          ? body.multimediaList[0]
          : body?.multimedia || null;
        if (!rawItem) {
          fallbackUrls.push(url);
          continue;
        }
        importedFromUrl.push({
          id: rawItem?.id || "",
          sourceId: rawItem?.id || "",
          url: rawItem?.url || "",
          mediaType: rawItem?.mediaType || "",
          mimeType: rawItem?.mimeType || "",
          language: String(languageIso || "").toLowerCase(),
          mediaRole: String(mediaRole || ""),
        });
      } catch (error) {
        fallbackUrls.push(url);
      }
    }
    if (!fallbackUrls.length) return importedFromUrl;
    const files = [];
    for (let index = 0; index < fallbackUrls.length; index += 1) {
      try {
        const file = await downloadImportedMediaAsFile(
          fallbackUrls[index],
          `${baseName}-${index + 1}`,
          mimeFallback,
        );
        files.push(file);
      } catch (error) {
        // Keep importing the rest of the JSON even if one remote asset fails.
      }
    }
    if (!files.length) return importedFromUrl;
    const uploadedFiles = await uploadModePointMediaFiles(files, {
      language: languageIso,
      mediaRole,
    });
    return [
      ...importedFromUrl,
      ...(Array.isArray(uploadedFiles) ? uploadedFiles : []),
    ];
  }

  async function handleImportModeJson(file) {
    if (!file || isImportingModeJson) return;
    setModeJsonImportError("");
    setModeJsonImportMessage("");
    if (!selectedStartPoint?.id || !selectedMode) {
      setModeJsonImportError("Select a Route Type before importing JSON.");
      return;
    }
    if (!/\.json$/i.test(String(file?.name || ""))) {
      setModeJsonImportError("Only JSON files are allowed.");
      return;
    }
    const activeRouteKey = getActiveModeImportRouteKey(selectedMode);
    if (selectedMode === "walking" && !String(activeRouteKey || "").trim()) {
      setModeJsonImportError("Select a walking route before importing JSON.");
      return;
    }
    if (
      Array.isArray(activeModePoints) &&
      activeModePoints.length &&
      !window.confirm(
        "Are you sure you want to replace the current route points with this JSON file?",
      )
    ) {
      return;
    }
    setIsImportingModeJson(true);
    setModeJsonImportMessage("Reading JSON file...");
    try {
      const rawText = await file.text();
      const parsed = JSON.parse(rawText);
      const rawPoints = Array.isArray(parsed?.points) ? parsed.points : [];
      if (!rawPoints.length) {
        throw new Error('The JSON must contain a non-empty "points" array.');
      }

      const importedPoints = [];
      for (let index = 0; index < rawPoints.length; index += 1) {
        setModeJsonImportMessage(
          `Importing point ${index + 1} of ${rawPoints.length}...`,
        );
        const item = rawPoints[index] || {};
        const pointOrder =
          Number(item?.pointNumber ?? item?.number_point ?? index + 1) ||
          index + 1;
        const routeGroup =
          selectedMode === "driving"
            ? normalizeImportedRouteGroup(item?.routeGroup)
            : selectedMode === "walking"
              ? "main"
              : "main";
        const localPointId =
          selectedMode === "walking"
            ? `point-${String(activeRouteKey || "w")}-${pointOrder}`
            : `point-${pointOrder}`;

        const localizedName = normalizeImportedLocalizedField(
          item?.title ?? item?.name,
          languages,
        );
        const localizedDescription = normalizeImportedLocalizedField(
          item?.descriptions ?? item?.description,
          languages,
          formatImportedRichText,
        );
        const localizedMoreInfo = normalizeImportedLocalizedField(
          item?.infoText,
          languages,
          formatImportedRichText,
        );
        const latitude = Number(item?.coordinate?.latitude ?? item?.latitude);
        const longitude = Number(
          item?.coordinate?.longitude ?? item?.longitude,
        );
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          continue;
        }

        const photoUrls = Array.isArray(item?.media?.photoUrls)
          ? item.media.photoUrls.filter(Boolean)
          : [];
        if (photoUrls.length) {
          setModeJsonImportMessage(
            `Importing images for point ${index + 1} of ${rawPoints.length}...`,
          );
        }
        const uploadedImages = await uploadImportedMediaUrls(photoUrls, {
          baseName: `point-${pointOrder}-image`,
          mediaRole: "image",
        });

        const audioEntries = Object.entries(
          item?.media?.audioUrls || {},
        ).filter(([, url]) => Boolean(url));
        const uploadedAudios = [];
        for (const [iso, url] of audioEntries) {
          const normalizedIso = String(iso || "")
            .toLowerCase()
            .trim();
          const languageExists = (
            Array.isArray(languages) ? languages : []
          ).some(
            (language) =>
              String(language?.lng_iso || language?.iso || "")
                .toLowerCase()
                .trim() === normalizedIso,
          );
          if (!languageExists) continue;
          setModeJsonImportMessage(
            `Importing ${normalizedIso.toUpperCase()} audio for point ${index + 1} of ${rawPoints.length}...`,
          );
          const uploaded = await uploadImportedMediaUrls([url], {
            baseName: `point-${pointOrder}-audio-${normalizedIso}`,
            languageIso: normalizedIso,
            mediaRole: "audio",
            mimeFallback: "audio/mpeg",
          });
          uploadedAudios.push(...uploaded);
        }

        importedPoints.push({
          id: localPointId,
          order: pointOrder,
          latitude,
          longitude,
          name: localizedName,
          description: localizedDescription,
          more_info: localizedMoreInfo,
          notes: String(item?.notes || ""),
          radiusMeters: Number(item?.triggerRadiusMeters ?? 12) || 12,
          turnCase: Number(item?.turnType ?? 0) || 0,
          pointType:
            selectedMode === "random"
              ? String(item?.pointType || "building")
              : "",
          routeGroup,
          routeGroupName:
            selectedMode === "driving"
              ? buildDrivingRouteGroupName(pointOrder, routeGroup)
              : "",
          routeGroupParentPointId: "",
          labelCode: String(item?.labelCode || ""),
          multimedia: [...uploadedImages, ...uploadedAudios],
          isPersisted: false,
        });
      }

      if (!importedPoints.length) {
        throw new Error("The JSON does not contain points with valid coordinates.");
      }

      const sortedPoints = importedPoints.sort(
        (a, b) => Number(a?.order || 0) - Number(b?.order || 0),
      );

      dispatch(
        setModePointsForStartMode({
          startPointId: selectedStartPoint.id,
          mode: selectedMode,
          routeKey: activeRouteKey,
          points: sortedPoints,
        }),
      );
      setModeJsonImportMessage("Drawing imported points on the map...");
      suppressNextModePointAutoSelect();
      setSelectedModePointId("");
      setPointsFromList(sortedPoints, {
        markerColor: "#603d1c",
        markerVariant:
          selectedMode === "driving"
            ? "mode-driving"
            : selectedMode === "walking"
              ? "mode-walking"
              : selectedMode === "random"
                ? "mode-random"
                : "default",
        startPointMarker:
          selectedMode === "driving" && selectedStartPoint
            ? selectedStartPoint
            : null,
        includeStartPointInBounds: selectedMode === "driving",
        autoFitBounds: true,
      });
      dispatch(resetDirectiosApiRoutePoint());
      dispatch(resetDirectiosApiRoutePointRaw());
      autoRouteRequestRef.current = "";
      autoRouteBlockedRef.current = "";
      if (selectedMode !== "random") {
        setModeJsonImportMessage("Calculating the imported route...");
        await handleBuildDrivingRoute(
          sortedPoints.map((point) => ({
            ...point,
            number_point: Number(point?.order || 0),
          })),
          {
            mode: selectedMode,
            routeKey: activeRouteKey,
          },
        );
      }
      setModeJsonImportMessage(
        `${sortedPoints.length} route points imported successfully.`,
      );
    } catch (error) {
      setModeJsonImportError(
        error?.message || "Could not import Route Type JSON.",
      );
      setModeJsonImportMessage("");
    } finally {
      setIsImportingModeJson(false);
    }
  }

  function clearMapBoundariesArtifacts() {
    setIsPickingBoundaryPoints(false);
    clearBoundaryPolygon();
    clearAllPoints();
  }

  function handleAddDrivingModePoint(point) {
    if (!point?.id) return;
    const routeTypes = extractRouteTypes(point);
    const normalizedTypes = routeTypes
      .map((item) => normalizeModeType(item?.type))
      .filter(Boolean);
    const nextModeType = normalizedTypes.includes("driving")
      ? "driving"
      : normalizedTypes[0] || "driving";
    handleClearPreviewRoutesOnMap();
    clearMapBoundariesArtifacts();
    closeMapBoundariesState();
    setSelectedStartPoint(point);
    setIsWalkingFromTourMode(false);
    setIsPickingStartPoints(false);
    setIsPickingModePoints(false);
    setIsModeToolOpen(true);
    setAutoExpandStartPointId("");
    selectStartPointForModeTool(point);
    setPendingModeType(nextModeType);
    setPendingDrivingPointId(point.id);
  }

  function handleCloseModeTool() {
    loadedModeContextRef.current = "";
    activeModeLoadRequestRef.current = "";
    hydratedWithoutRouteSignatureByScopeRef.current = {};
    autoRouteRequestRef.current = "";
    autoRouteBlockedRef.current = "";
    closeActivePointPopup();
    resetToolState();
    dispatch(resetDirectiosApiRoutePoint());
    dispatch(resetDirectiosApiRoutePointRaw());
    forceDrawExternalRoute(null);
    drawExternalRouteWithRetry(null);
    clearRouteGeometry();
    handleClearPreviewRoutesOnMap();
    setRouteColor("#38bdf8");
    setRouteDashArray([1, 0]);
    setSelectedStartPoint(null);
    setIsModeToolOpen(false);
    setIsPickingModePoints(false);
    setIsWalkingRouteShapeEditEnabled(false);
    setAutoExpandStartPointId("");
    setPendingModeType("driving");
    setPendingDrivingPointId("");
    setSelectedRandomRouteTypeId("");
    setModeJsonImportMessage("");
    setModeJsonImportError("");
    setAccordionResetKey((prev) => prev + 1);
    selectStartPointForModeTool(null);
    setRouteReadySignatureByRoute({});
    if (isWalkingFromTourMode) {
      clearAllPoints();
      setIsPointMode(false);
      setSelectedStartTour(null);
      setIsWalkingFromTourMode(false);
      return;
    }
    setMarkerVariant("start-point");
    setPointsFromList(startPoints || [], {
      markerColor: "#ef4444",
      markerVariant: "start-point",
    });
  }

  function handleTogglePickingStartPoints() {
    setIsPickingModePoints(false);
    setMarkerVariant("start-point");
    setIsPickingStartPoints((prev) => {
      const next = !prev;
      if (next) {
        setAutoExpandStartPointId("");
        previousPointsCountRef.current = Array.isArray(startPoints)
          ? startPoints.length
          : 0;
      }
      return next;
    });
  }

  function handleTogglePickingModePoints() {
    setIsPickingStartPoints(false);
    setAutoExpandStartPointId("");
    setIsWalkingRouteShapeEditEnabled(false);
    setIsPickingModePoints((prev) => !prev);
  }

  function handleSelectModePoint(pointId) {
    setSelectedModePointId(pointId);
    focusPointById(pointId, { openPopup: true, zoom: 18.5 });
  }

  function splitDirectionsCoordinatesIntoChunks(
    coordinates = [],
    maxCoordinatesPerRequest = 25,
  ) {
    const safeCoordinates = Array.isArray(coordinates) ? coordinates : [];
    if (safeCoordinates.length <= maxCoordinatesPerRequest) {
      return [safeCoordinates];
    }
    const chunks = [];
    const step = Math.max(2, maxCoordinatesPerRequest - 1);
    for (let startIndex = 0; startIndex < safeCoordinates.length - 1; ) {
      const endIndex = Math.min(
        startIndex + maxCoordinatesPerRequest,
        safeCoordinates.length,
      );
      const chunk = safeCoordinates.slice(startIndex, endIndex);
      if (chunk.length >= 2) {
        chunks.push(chunk);
      }
      if (endIndex >= safeCoordinates.length) break;
      startIndex += step;
    }
    return chunks;
  }

  function mergeDirectionsSegmentRoutes(segmentRoutes = []) {
    const validRoutes = (
      Array.isArray(segmentRoutes) ? segmentRoutes : []
    ).filter((route) => Array.isArray(route?.geometry?.coordinates));
    if (!validRoutes.length) return null;
    const mergedCoordinates = [];
    validRoutes.forEach((route, routeIndex) => {
      const coords = Array.isArray(route?.geometry?.coordinates)
        ? route.geometry.coordinates
        : [];
      coords.forEach((coord, coordIndex) => {
        if (
          routeIndex > 0 &&
          coordIndex === 0 &&
          mergedCoordinates.length &&
          mergedCoordinates[mergedCoordinates.length - 1]?.[0] === coord?.[0] &&
          mergedCoordinates[mergedCoordinates.length - 1]?.[1] === coord?.[1]
        ) {
          return;
        }
        mergedCoordinates.push(coord);
      });
    });
    if (mergedCoordinates.length < 2) return null;
    return {
      geometry: {
        type: "LineString",
        coordinates: mergedCoordinates,
      },
      distance: validRoutes.reduce(
        (total, route) => total + Number(route?.distance || 0),
        0,
      ),
      duration: validRoutes.reduce(
        (total, route) => total + Number(route?.duration || 0),
        0,
      ),
    };
  }

  async function buildHybridWalkingRoute(
    sortedPoints = [],
    shapePoints = [],
    profile = "walking",
  ) {
    const mapboxProfile = profile === "driving" ? "driving" : "walking";
    const pointsList = [...(Array.isArray(sortedPoints) ? sortedPoints : [])]
      .map((point) => ({
        ...point,
        number_point: Number(point?.number_point || point?.order || 0) || 0,
        longitude: point?.longitude ?? point?.lng,
        latitude: point?.latitude ?? point?.lat,
      }))
      .filter((point) => {
        const lng = Number(point?.longitude);
        const lat = Number(point?.latitude);
        return (
          point.number_point > 0 && Number.isFinite(lng) && Number.isFinite(lat)
        );
      })
      .sort((a, b) => Number(a.number_point) - Number(b.number_point));
    if (pointsList.length < 2) return null;
    const shapeByAfterOrder = (Array.isArray(shapePoints) ? shapePoints : [])
      .filter((point) => {
        const lng = Number(point?.longitude ?? point?.lng);
        const lat = Number(point?.latitude ?? point?.lat);
        return Number.isFinite(lng) && Number.isFinite(lat);
      })
      .reduce((acc, point) => {
        const afterOrder = Number(point?.afterPointOrder || 0) || 1;
        if (!acc[afterOrder]) acc[afterOrder] = [];
        acc[afterOrder].push(point);
        return acc;
      }, {});
    const segmentRoutes = [];
    for (let index = 0; index < pointsList.length - 1; index += 1) {
      const startPoint = pointsList[index];
      const endPoint = pointsList[index + 1];
      const startCoords = [
        Number(startPoint.longitude),
        Number(startPoint.latitude),
      ];
      const endCoords = [Number(endPoint.longitude), Number(endPoint.latitude)];
      const segmentShapePoints = (
        shapeByAfterOrder[startPoint.number_point] || []
      ).sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0));
      const manualCoordinates = [
        startCoords,
        ...segmentShapePoints.map((point) => [
          Number(point?.longitude ?? point?.lng),
          Number(point?.latitude ?? point?.lat),
        ]),
        endCoords,
      ].filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
      if (segmentShapePoints.length && mapboxProfile === "walking") {
        if (manualCoordinates.length >= 2) {
          segmentRoutes.push({
            geometry: {
              type: "LineString",
              coordinates: manualCoordinates,
            },
            distance: 0,
            duration: 0,
          });
        }
        continue;
      }
      const coordsPath = (
        segmentShapePoints.length ? manualCoordinates : [startCoords, endCoords]
      )
        .map(([lng, lat]) => `${lng},${lat}`)
        .join(";");
      const requestParams = new URLSearchParams({
        geometries: "geojson",
        overview: "full",
        steps: "true",
        access_token: String(process.env.REACT_APP_MAPBOX_TOKEN || ""),
        alternatives: "false",
      });
      const url = `https://api.mapbox.com/directions/v5/mapbox/${mapboxProfile}/${coordsPath}?${requestParams.toString()}`;
      const response = await fetch(url);
      const data = await response.json();
      const route = Array.isArray(data?.routes) ? data.routes[0] : null;
      if (route?.geometry?.coordinates?.length) {
        segmentRoutes.push(route);
      }
    }
    return mergeDirectionsSegmentRoutes(segmentRoutes);
  }

  function reduceDrivingPointsToRoutingAnchors(pointsList = []) {
    const safePoints = [...(Array.isArray(pointsList) ? pointsList : [])].sort(
      (a, b) =>
        Number(a?.number_point ?? a?.order ?? 0) -
        Number(b?.number_point ?? b?.order ?? 0),
    );
    if (safePoints.length <= 2) return safePoints;
    return safePoints.filter((point, index, list) => {
      const lng = Number(point?.longitude ?? point?.lng);
      const lat = Number(point?.latitude ?? point?.lat);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
      if (index === 0) return true;
      const previousPoint = list[index - 1];
      const previousLng = Number(
        previousPoint?.longitude ?? previousPoint?.lng,
      );
      const previousLat = Number(previousPoint?.latitude ?? previousPoint?.lat);
      return lng !== previousLng || lat !== previousLat;
    });
  }

  function getRoutePointSequenceValue(point = {}, fallbackIndex = 0) {
    return (
      Number(point?.order ?? point?.number_point ?? fallbackIndex + 1) ||
      fallbackIndex + 1
    );
  }

  function buildDrivingRouteGroupsForDirections(pointsList = []) {
    const sortedPoints = [
      ...(Array.isArray(pointsList) ? pointsList : []),
    ].sort(
      (a, b) => getRoutePointSequenceValue(a) - getRoutePointSequenceValue(b),
    );
    const resolvePointIdentity = (point = {}, fallbackIndex = 0) =>
      String(
        point?.id ||
          `${getRoutePointSequenceValue(point, fallbackIndex)}:${Number(
            point?.longitude ?? point?.lng ?? 0,
          ).toFixed(7)}:${Number(point?.latitude ?? point?.lat ?? 0).toFixed(
            7,
          )}`,
      );
    const uniqueRoutePoints = (points = []) => {
      const seen = new Set();
      return (Array.isArray(points) ? points : []).filter((point, index) => {
        if (!point) return false;
        const identity = resolvePointIdentity(point, index);
        if (seen.has(identity)) return false;
        seen.add(identity);
        return true;
      });
    };
    const mainPoints = sortedPoints.filter(
      (point) =>
        normalizeDrivingRouteGroupKey(
          point?.routeGroup ?? point?.route_group ?? "",
          point?.routeGroupName ?? point?.route_group_name ?? "",
          getRoutePointSequenceValue(point),
        ) === "main",
    );
    const routeGroups = [];
    if (mainPoints.length >= 2) {
      routeGroups.push({
        routeGroup: "main",
        points: mainPoints,
      });
    }

    let currentAlternateKey = "";
    let currentAlternatePoints = [];

    const flushAlternateGroup = () => {
      if (!currentAlternateKey || currentAlternatePoints.length < 1) return;
      const uniqueBranchPoints = uniqueRoutePoints(currentAlternatePoints);
      if (uniqueBranchPoints.length >= 2) {
        routeGroups.push({
          routeGroup: currentAlternateKey,
          points: uniqueBranchPoints,
        });
      }
      currentAlternateKey = "";
      currentAlternatePoints = [];
    };

    sortedPoints.forEach((point) => {
      const routeGroup = normalizeDrivingRouteGroupKey(
        point?.routeGroup ?? point?.route_group ?? "",
        point?.routeGroupName ?? point?.route_group_name ?? "",
        getRoutePointSequenceValue(point),
      );
      if (routeGroup === "main") {
        flushAlternateGroup();
        return;
      }
      if (!currentAlternateKey) {
        currentAlternateKey = routeGroup;
        currentAlternatePoints = [point];
        return;
      }
      if (currentAlternateKey !== routeGroup) {
        flushAlternateGroup();
        currentAlternateKey = routeGroup;
        currentAlternatePoints = [point];
        return;
      }
      currentAlternatePoints.push(point);
    });

    flushAlternateGroup();
    return routeGroups;
  }

  async function handleBuildDrivingRoute(overridePoints = null, options = {}) {
    // Uses organized redux points to call Mapbox Directions for the active mode/route.
    const modeForBuild = String(
      options?.mode || selectedMode || "",
    ).toLowerCase();
    const routeKeyForBuild = String(
      options?.routeKey ??
        (modeForBuild === "walking" ? selectedWalkingRouteKey : "default"),
    );
    const organized = Array.isArray(overridePoints)
      ? overridePoints
      : Array.isArray(selectedModePointsDraft)
        ? selectedModePointsDraft
        : [];
    const sorted = [...organized].sort(
      (a, b) => Number(a?.number_point || 0) - Number(b?.number_point || 0),
    );
    const isWalkingMode = modeForBuild === "walking";
    const coordinates = sorted
      .map((item) => [Number(item?.longitude), Number(item?.latitude)])
      .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
    if (!process.env.REACT_APP_MAPBOX_TOKEN) return;
    if (coordinates.length < 2) return;
    const mapboxProfile = isWalkingMode ? "walking" : "driving";
    const drivingRouteGroupsForBuild = !isWalkingMode
      ? buildDrivingRouteGroupsForDirections(sorted).filter(
          (group) =>
            (Array.isArray(group?.points) ? group.points : []).filter(
              (item) =>
                Number.isFinite(Number(item?.longitude)) &&
                Number.isFinite(Number(item?.latitude)),
            ).length >= 2,
        )
      : [];
    const hasDrivingAlternateRoutes = drivingRouteGroupsForBuild.some(
      (group) =>
        normalizeDrivingRouteGroupKey(group?.routeGroup || "") !== "main",
    );
    try {
      const activeShapePoints =
        isWalkingMode || modeForBuild === "driving"
          ? getActiveRouteShapePoints(modeForBuild, routeKeyForBuild)
          : [];
      if (
        (isWalkingMode ||
          (modeForBuild === "driving" && !hasDrivingAlternateRoutes)) &&
        activeShapePoints.length
      ) {
        const hybridRoute = await buildHybridWalkingRoute(
          sorted,
          activeShapePoints,
          mapboxProfile,
        );
        if (!hybridRoute?.geometry) return;
        setRouteDashArray(isWalkingMode ? [2, 2] : [1, 0]);
        const routeShapePayload = activeShapePoints.map((point, index) => ({
          id: String(point?.id || ""),
          latitude: String(point?.latitude ?? point?.lat ?? ""),
          longitude: String(point?.longitude ?? point?.lng ?? ""),
          after_point_order: Number(point?.afterPointOrder || 0) || 0,
          point_id: String(point?.pointId ?? point?.point_id ?? ""),
          label_code: String(point?.labelCode ?? point?.label_code ?? ""),
          segment_ratio:
            Number(point?.segmentRatio ?? point?.segment_ratio) || 0,
          order: Number(point?.order || index + 1) || index + 1,
        }));
        const rawPayload = {
          profile: mapboxProfile,
          is_manual_route_shape: true,
          route_shape_points: routeShapePayload,
          routes: {
            main: {
              geometry: hybridRoute.geometry,
              distance: Number(hybridRoute?.distance || 0),
              duration: Number(hybridRoute?.duration || 0),
            },
          },
        };
        dispatch(setDirectiosApiRoutePointRaw(rawPayload));
        dispatch(
          setDirectiosApiRoutePoint({
            profile: mapboxProfile,
            points: sorted,
            geometry: hybridRoute.geometry,
            distanceMeters: Number(hybridRoute?.distance || 0),
            durationSeconds: Number(hybridRoute?.duration || 0),
          }),
        );
        forceDrawExternalRoute(hybridRoute.geometry);
        drawExternalRouteWithRetry(hybridRoute.geometry);
        const scopeKey = getActiveRouteScopeKey(modeForBuild, routeKeyForBuild);
        delete hydratedWithoutRouteSignatureByScopeRef.current[scopeKey];
        const nextSignature = buildWalkingRouteSignature(
          sorted,
          activeShapePoints,
        );
        setRouteReadySignatureByRoute((prev) => {
          if ((prev?.[scopeKey] || "") === nextSignature) return prev;
          return {
            ...prev,
            [scopeKey]: nextSignature,
          };
        });
        return;
      }
      const routeGroups = isWalkingMode
        ? [{ routeGroup: "walking", points: sorted }]
        : drivingRouteGroupsForBuild;
      if (!routeGroups.length) return;
      const groupResponses = [];
      for (const routeGroup of routeGroups) {
        const routeGroupPoints = Array.isArray(routeGroup?.points)
          ? routeGroup.points
          : [];
        const routeGroupPointOrders = new Set(
          routeGroupPoints.map((point) =>
            Number(point?.number_point || point?.order || 0),
          ),
        );
        const routeGroupShapePoints =
          !isWalkingMode && activeShapePoints.length
            ? activeShapePoints.filter((point) =>
                routeGroupPointOrders.has(Number(point?.afterPointOrder || 0)),
              )
            : [];
        if (routeGroupShapePoints.length) {
          const hybridRoute = await buildHybridWalkingRoute(
            routeGroupPoints,
            routeGroupShapePoints,
            mapboxProfile,
          );
          if (hybridRoute?.geometry?.coordinates?.length) {
            groupResponses.push({
              routeGroup: routeGroup?.routeGroup || "main",
              points: routeGroupPoints,
              raw: {
                profile: mapboxProfile,
                segmentCount: 1,
                isManualRouteShape: true,
              },
              route: hybridRoute,
            });
            continue;
          }
        }
        const groupCoordinates = routeGroupPoints
          .map((item) => [Number(item?.longitude), Number(item?.latitude)])
          .filter(
            (pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]),
          );
        if (groupCoordinates.length < 2) continue;
        const coordinateChunks =
          splitDirectionsCoordinatesIntoChunks(groupCoordinates);
        const segmentRoutes = [];
        for (const chunkCoordinates of coordinateChunks) {
          const coordsPath = chunkCoordinates
            .map(([lng, lat]) => `${lng},${lat}`)
            .join(";");
          const requestParams = new URLSearchParams({
            geometries: "geojson",
            overview: "full",
            steps: "true",
            access_token: String(process.env.REACT_APP_MAPBOX_TOKEN || ""),
            alternatives: "false",
          });
          if (!isWalkingMode) {
            requestParams.set("continue_straight", "true");
            requestParams.set("annotations", "distance,duration");
          }
          const url = `https://api.mapbox.com/directions/v5/mapbox/${mapboxProfile}/${coordsPath}?${requestParams.toString()}`;
          const response = await fetch(url);
          const data = await response.json();
          const route = Array.isArray(data?.routes) ? data.routes[0] : null;
          if (!route?.geometry?.coordinates?.length) continue;
          segmentRoutes.push(route);
        }
        const route = mergeDirectionsSegmentRoutes(segmentRoutes);
        if (!route?.geometry?.coordinates?.length) continue;
        groupResponses.push({
          routeGroup: routeGroup?.routeGroup || "main",
          points: routeGroupPoints,
          raw: {
            profile: mapboxProfile,
            segmentCount: segmentRoutes.length,
          },
          route,
        });
      }
      if (!groupResponses.length) return;
      setRouteDashArray(isWalkingMode ? [2, 2] : [1, 0]);
      const multiGeometry =
        groupResponses.length === 1
          ? groupResponses[0].route.geometry
          : {
              type: "MultiLineString",
              coordinates: groupResponses.map(
                (item) => item.route.geometry.coordinates,
              ),
            };
      const normalizedRouteMap = groupResponses.reduce((acc, item) => {
        const normalizedKey = isWalkingMode
          ? "main"
          : normalizeDrivingRouteGroupKey(item?.routeGroup || "");
        acc[normalizedKey] = {
          geometry: item?.route?.geometry || null,
          distance: Number(item?.route?.distance || 0),
          duration: Number(item?.route?.duration || 0),
        };
        return acc;
      }, {});
      const routeShapePayload = activeShapePoints.map((point, index) => ({
        id: String(point?.id || ""),
        latitude: String(point?.latitude ?? point?.lat ?? ""),
        longitude: String(point?.longitude ?? point?.lng ?? ""),
        after_point_order: Number(point?.afterPointOrder || 0) || 0,
        point_id: String(point?.pointId ?? point?.point_id ?? ""),
        label_code: String(point?.labelCode ?? point?.label_code ?? ""),
        segment_ratio:
          Number(point?.segmentRatio ?? point?.segment_ratio) || 0,
        order: Number(point?.order || index + 1) || index + 1,
      }));
      const rawPayload = {
        profile: mapboxProfile,
        routes: normalizedRouteMap,
        ...(routeShapePayload.length
          ? {
              is_manual_route_shape: true,
              route_shape_points: routeShapePayload,
            }
          : {}),
      };
      dispatch(setDirectiosApiRoutePointRaw(rawPayload));
      const routePayload = {
        profile: mapboxProfile,
        points: sorted,
        geometry: multiGeometry,
        distanceMeters: Number(rawPayload.distance || 0),
        durationSeconds: Number(rawPayload.duration || 0),
      };
      dispatch(setDirectiosApiRoutePoint(routePayload));
      const scopeKey = getActiveRouteScopeKey(modeForBuild, routeKeyForBuild);
      delete hydratedWithoutRouteSignatureByScopeRef.current[scopeKey];
      const nextSignature = activeShapePoints.length
        ? buildWalkingRouteSignature(sorted, activeShapePoints)
        : buildModePointsSignature(sorted);
      setRouteReadySignatureByRoute((prev) => {
        if ((prev?.[scopeKey] || "") === nextSignature) return prev;
        return {
          ...prev,
          [scopeKey]: nextSignature,
        };
      });
    } catch (error) {
      // Silent fail to avoid blocking mode-tool editing flow.
    }
  }

  async function loadModePointsFromApi(
    tourTypeId,
    modeValue,
    startPointId,
    routeKey = "default",
  ) {
    // Hydrates mode points + optional stored Directions route from backend by tour type id.
    if (!tourTypeId || !modeValue || !startPointId) return;
    const requestKey = `${String(tourTypeId || "")}::${String(modeValue || "")}::${String(startPointId || "")}::${String(routeKey || "default")}`;
    activeModeLoadRequestRef.current = requestKey;
    hydratingModeLoadRequestRef.current = requestKey;
    closeActivePointPopup();
    clearAllPoints();
    dispatch(resetDirectiosApiRoutePoint());
    dispatch(resetDirectiosApiRoutePointRaw());
    forceDrawExternalRoute(null);
    drawExternalRouteWithRetry(null);
    clearRouteGeometry();
    try {
      if (modeValue === "walking") {
        setRouteColor("#588354");
        setRouteDashArray([2, 2]);
        const walkingResponse =
          await listWalkingRoutesByTourRequest(tourTypeId);
        if (isStaleModeLoad(requestKey, startPointId)) return;
        const walkingRoutes = Array.isArray(walkingResponse?.body?.routes)
          ? walkingResponse.body.routes
          : [];
        const mappedRoutes = walkingRoutes.map((route) => ({
          routeKey: String(route?.startRouteTypeId || ""),
          name: route?.name || route?.routeName || route?.route_name || {},
          route_shape_points:
            route?.route_shape_points ||
            route?.routeShapePoints ||
            route?.shape_points ||
            [],
          points: (Array.isArray(route?.points) ? route.points : []).map(
            (point) => normalizeTourPointFromApi(point),
          ),
        }));
        dispatch(
          setWalkingRoutesSnapshot({
            ownerId: startPointId,
            routes: mappedRoutes,
          }),
        );
        const resolvedRouteKey =
          walkingRoutes.find(
            (route) =>
              String(route?.startRouteTypeId || "") === String(routeKey || ""),
          )?.startRouteTypeId ||
          walkingRoutes[0]?.startRouteTypeId ||
          "";
        if (
          walkingRoutes.length &&
          resolvedRouteKey &&
          String(resolvedRouteKey) !== String(selectedWalkingRouteKey || "")
        ) {
          selectWalkingRoute(String(resolvedRouteKey));
        } else if (!walkingRoutes.length) {
          selectWalkingRoute("");
        }
        const activeRoute = walkingRoutes.find(
          (route) =>
            String(route?.startRouteTypeId || "") ===
            String(resolvedRouteKey || ""),
        );
        const activeShapePoints = normalizeRouteShapePointsFromApi(
          Array.isArray(activeRoute?.route_shape_points)
            ? activeRoute.route_shape_points
            : Array.isArray(activeRoute?.routeShapePoints)
              ? activeRoute.routeShapePoints
              : Array.isArray(activeRoute?.shape_points)
                ? activeRoute.shape_points
                : Array.isArray(
                      activeRoute?.directiosApiRoutePointRaw
                        ?.route_shape_points,
                    )
                  ? activeRoute.directiosApiRoutePointRaw.route_shape_points
                  : [],
        );
        setWalkingRouteShapePointsByKey((prev) => ({
          ...prev,
          [getActiveRouteShapeKey("walking", resolvedRouteKey || routeKey)]:
            activeShapePoints,
        }));
        const activePoints = Array.isArray(activeRoute?.points)
          ? activeRoute.points
          : [];
        const normalizedActivePoints = activePoints.map((point) =>
          normalizeTourPointFromApi(point),
        );
        suppressNextModePointAutoSelect();
        setPointsFromList(normalizedActivePoints, {
          markerColor: "#603d1c",
          markerVariant: "mode-walking",
          startPointMarker: null,
          autoFitBounds: true,
        });
        const raw = resolveRawDirectionsPayload(
          activeRoute?.directiosApiRoutePointRaw ??
            directiosApiRoutePointRaw ??
            null,
        );
        const scopeKey = getActiveRouteScopeKey(
          modeValue,
          resolvedRouteKey || routeKey,
        );
        if (raw) {
          dispatch(setDirectiosApiRoutePointRaw(raw));
        }
        const route = resolveRouteFromRaw(raw, {
          routeKey: resolvedRouteKey || routeKey,
        });
        if (route?.geometry) {
          delete hydratedWithoutRouteSignatureByScopeRef.current[scopeKey];
          forceDrawExternalRoute(route.geometry);
          drawExternalRouteWithRetry(route.geometry);
          dispatch(
            setDirectiosApiRoutePoint({
              profile: "walking",
              points: normalizedActivePoints.map((point) => ({
                number_point: Number(point?.order || 0),
                latitude: String(point?.latitude ?? ""),
                longitude: String(point?.longitude ?? ""),
              })),
              geometry: route.geometry,
              distanceMeters: Number(route?.distance || 0),
              durationSeconds: Number(route?.duration || 0),
            }),
          );
          if (normalizedActivePoints.length >= 2) {
            await handleBuildDrivingRoute(
              normalizedActivePoints.map((point) => ({
                ...point,
                number_point: Number(point?.order || point?.number_point || 0),
              })),
              {
                mode: "walking",
                routeKey: resolvedRouteKey || routeKey,
              },
            );
          }
        } else {
          hydratedWithoutRouteSignatureByScopeRef.current[scopeKey] =
            buildModePointsSignature(activePoints);
          dispatch(resetDirectiosApiRoutePoint());
          forceDrawExternalRoute(null);
          drawExternalRouteWithRetry(null);
          clearRouteGeometry();
        }
        setRouteReadySignatureByRoute((prev) => ({
          ...prev,
          [scopeKey]: buildModePointsSignature(activePoints),
        }));
        return;
      }
      if (modeValue === "random") {
        setRouteColor("#4faede");
        setRouteDashArray([1, 0]);
        const randomResponse = await listRandomRouteByTourRequest(tourTypeId);
        if (isStaleModeLoad(requestKey, startPointId)) return;
        const randomBody = randomResponse?.body || {};
        const randomRoute =
          randomBody?.route ||
          (Array.isArray(randomBody?.routes) ? randomBody.routes[0] : null);
        const apiPoints = Array.isArray(randomRoute?.points)
          ? randomRoute.points
          : [];
        const normalizedPoints = apiPoints.map((point) =>
          normalizeTourPointFromApi(point),
        );
        dispatch(
          setModePointsForStartMode({
            startPointId,
            mode: "random",
            routeKey: "default",
            points: normalizedPoints,
          }),
        );
        setSelectedRandomRouteTypeId(
          String(randomRoute?.startRouteTypeId || ""),
        );
        suppressNextModePointAutoSelect();
        setPointsFromList(normalizedPoints, {
          markerColor: "#603d1c",
          markerVariant: "mode-random",
          startPointMarker: null,
          autoFitBounds: true,
        });
        const raw = resolveRawDirectionsPayload(
          randomRoute?.directiosApiRoutePointRaw ??
            directiosApiRoutePointRaw ??
            null,
        );
        if (raw) {
          dispatch(setDirectiosApiRoutePointRaw(raw));
        }
        const route = resolveRouteFromRaw(raw, {
          routeKey,
        });
        if (route?.geometry) {
          forceDrawExternalRoute(route.geometry);
          drawExternalRouteWithRetry(route.geometry);
          dispatch(
            setDirectiosApiRoutePoint({
              profile: "random",
              points: normalizedPoints.map((point) => ({
                number_point: Number(point?.order || 0),
                latitude: String(point?.latitude ?? ""),
                longitude: String(point?.longitude ?? ""),
              })),
              geometry: route.geometry,
              distanceMeters: Number(route?.distance || 0),
              durationSeconds: Number(route?.duration || 0),
            }),
          );
        } else {
          dispatch(resetDirectiosApiRoutePoint());
          forceDrawExternalRoute(null);
          drawExternalRouteWithRetry(null);
          clearRouteGeometry();
        }
        const scopeKey = getActiveRouteScopeKey(modeValue, "default");
        const nextSignature = buildModePointsSignature(apiPoints);
        setRouteReadySignatureByRoute((prev) => {
          if ((prev?.[scopeKey] || "") === nextSignature) return prev;
          return {
            ...prev,
            [scopeKey]: nextSignature,
          };
        });
        return;
      }
      if (modeValue === "driving") {
        setRouteColor("#4faede");
        setRouteDashArray([1, 0]);
      }
      const response = await listTourPointsByTourTypeRequest(
        tourTypeId,
        modeValue,
        routeKey,
      );
      if (isStaleModeLoad(requestKey, startPointId)) return;
      const body = response?.body || {};
      const apiPoints = Array.isArray(body?.points) ? body.points : [];
      const normalizedPoints = apiPoints.map((point) =>
        normalizeTourPointFromApi(point),
      );
      dispatch(
        setModePointsForStartMode({
          startPointId,
          mode: modeValue,
          routeKey,
          points: normalizedPoints,
        }),
      );
      // Paints listed points in map the same way as created mode points.
      suppressNextModePointAutoSelect();
      setPointsFromList(normalizedPoints, {
        markerColor: "#603d1c",
        markerVariant:
          modeValue === "driving"
            ? "mode-driving"
            : modeValue === "walking"
              ? "mode-walking"
              : modeValue === "random"
                ? "mode-random"
                : "default",
        startPointMarker:
          modeValue === "driving" && selectedStartPoint
            ? selectedStartPoint
            : null,
        includeStartPointInBounds: modeValue === "driving",
        autoFitBounds: true,
      });
      const raw = resolveRawDirectionsPayload(
        body?.directiosApiRoutePointRaw ?? directiosApiRoutePointRaw ?? null,
      );
      const scopeKey = getActiveRouteScopeKey(modeValue, routeKey);
      if (raw) {
        dispatch(setDirectiosApiRoutePointRaw(raw));
      }
      const activeShapePoints =
        modeValue === "driving"
          ? normalizeRouteShapePointsFromApi(
              Array.isArray(body?.route_shape_points)
                ? body.route_shape_points
                : Array.isArray(body?.routeShapePoints)
                  ? body.routeShapePoints
                  : Array.isArray(body?.shape_points)
                    ? body.shape_points
                    : Array.isArray(raw?.route_shape_points)
                      ? raw.route_shape_points
                      : Array.isArray(raw?.routeShapePoints)
                        ? raw.routeShapePoints
                        : [],
            )
          : [];
      if (modeValue === "driving") {
        setWalkingRouteShapePointsByKey((prev) => ({
          ...prev,
          [getActiveRouteShapeKey("driving", "default")]: activeShapePoints,
        }));
      }
      const route = resolveRouteFromRaw(raw);
      if (route?.geometry) {
        delete hydratedWithoutRouteSignatureByScopeRef.current[scopeKey];
        forceDrawExternalRoute(route.geometry);
        drawExternalRouteWithRetry(route.geometry);
        dispatch(
          setDirectiosApiRoutePoint({
            profile: modeValue === "walking" ? "walking" : "driving",
            points: normalizedPoints.map((point) => ({
              number_point: Number(point?.order || 0),
              latitude: String(point?.latitude ?? ""),
              longitude: String(point?.longitude ?? ""),
            })),
            geometry: route.geometry,
            distanceMeters: Number(route?.distance || 0),
            durationSeconds: Number(route?.duration || 0),
          }),
        );
        const nextSignature =
          modeValue === "driving"
            ? buildWalkingRouteSignature(body?.points || [], activeShapePoints)
            : buildModePointsSignature(body?.points || []);
        setRouteReadySignatureByRoute((prev) => {
          if ((prev?.[scopeKey] || "") === nextSignature) return prev;
          return {
            ...prev,
            [scopeKey]: nextSignature,
          };
        });
        return;
      }
      dispatch(resetDirectiosApiRoutePoint());
      dispatch(resetDirectiosApiRoutePointRaw());
      forceDrawExternalRoute(null);
      drawExternalRouteWithRetry(null);
      clearRouteGeometry();
      hydratedWithoutRouteSignatureByScopeRef.current[scopeKey] =
        modeValue === "driving"
          ? buildWalkingRouteSignature(body?.points || [], activeShapePoints)
          : buildModePointsSignature(body?.points || []);
      setRouteReadySignatureByRoute((prev) => ({ ...prev, [scopeKey]: "" }));
    } finally {
      finishHydratingModeLoad(requestKey);
    }
  }

  function handleCommitModePoint(pointId, payload) {
    commitActiveModePoint(pointId, payload);
    // Force popup refresh right after committing Redux changes.
    setPopupRefreshToken((prev) => prev + 1);
  }

  function handleDeleteModePoint(pointId) {
    // Deletes point from redux + map and clears route to avoid stale geometry after point removal.
    removeActiveModePoint(pointId);
    removePointById(pointId);
    dispatch(resetDirectiosApiRoutePoint());
    dispatch(resetDirectiosApiRoutePointRaw());
    forceDrawExternalRoute(null);
    drawExternalRouteWithRetry(null);
    clearRouteGeometry();
    setRouteReadySignatureByRoute({});
  }

  function invalidateActiveWalkingRouteShape() {
    const scopeKey = getActiveRouteScopeKey(
      selectedMode,
      selectedMode === "walking" ? selectedWalkingRouteKey : "default",
    );
    setRouteReadySignatureByRoute((prev) => ({ ...prev, [scopeKey]: "" }));
    autoRouteRequestRef.current = "";
  }

  function handleAddWalkingRouteShapePoint(lngLat = {}) {
    if (selectedMode !== "walking" && selectedMode !== "driving") return;
    if (selectedMode === "walking" && !selectedWalkingRouteKey) return;
    const lng = Number(lngLat?.longitude ?? lngLat?.lng);
    const lat = Number(lngLat?.latitude ?? lngLat?.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const shapeKey = getActiveRouteShapeKey(
      selectedMode,
      selectedWalkingRouteKey,
    );
    const providedAfterPointOrder = Number(
      lngLat?.afterPointOrder ?? lngLat?.after_point_order,
    );
    const segmentInfo = Number.isFinite(providedAfterPointOrder)
      ? {
          afterPointOrder: providedAfterPointOrder,
          pointId: String(lngLat?.pointId ?? lngLat?.point_id ?? ""),
          labelCode: String(lngLat?.labelCode ?? lngLat?.label_code ?? ""),
          segmentRatio:
            Number(lngLat?.segmentRatio ?? lngLat?.segment_ratio) || 0,
        }
      : calculateNearestRouteSegmentInfo(
          { longitude: lng, latitude: lat },
          selectedModePointsDraft,
        );
    setWalkingRouteShapePointsByKey((prev) => {
      const current = Array.isArray(prev?.[shapeKey]) ? prev[shapeKey] : [];
      const nextPoint = {
        id: `shape-${Date.now()}-${current.length + 1}`,
        longitude: lng,
        latitude: lat,
        afterPointOrder: Number(segmentInfo?.afterPointOrder || 1) || 1,
        pointId: String(segmentInfo?.pointId || ""),
        labelCode: String(segmentInfo?.labelCode || ""),
        segmentRatio: Number(segmentInfo?.segmentRatio || 0) || 0,
        order: current.length + 1,
      };
      return {
        ...prev,
        [shapeKey]: normalizeRouteShapePointOrders([...current, nextPoint]),
      };
    });
    setIsWalkingRouteShapeEditEnabled(false);
    invalidateActiveWalkingRouteShape();
  }

  function handleMoveWalkingRouteShapePoint(shapePointId, lngLat = {}) {
    if (selectedMode !== "walking" && selectedMode !== "driving") return;
    if (selectedMode === "walking" && !selectedWalkingRouteKey) return;
    if (!shapePointId) return;
    const lng = Number(lngLat?.longitude ?? lngLat?.lng);
    const lat = Number(lngLat?.latitude ?? lngLat?.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    const shapeKey = getActiveRouteShapeKey(
      selectedMode,
      selectedWalkingRouteKey,
    );
    setWalkingRouteShapePointsByKey((prev) => {
      const current = Array.isArray(prev?.[shapeKey]) ? prev[shapeKey] : [];
      const nextPoints = current.map((point) => {
        if (String(point?.id || "") !== String(shapePointId)) return point;
        const currentAfterPointOrder =
          Number(point?.afterPointOrder || point?.after_point_order) || 0;
        const afterPointOrder =
          currentAfterPointOrder ||
          Number(lngLat?.afterPointOrder ?? lngLat?.after_point_order) ||
          calculateNearestRouteSegmentOrder(
            { longitude: lng, latitude: lat },
            selectedModePointsDraft,
          );
        const providedSegmentRatio = Number(
          lngLat?.segmentRatio ?? lngLat?.segment_ratio,
        );
        const fallbackSegmentRatio = calculateRouteSegmentRatio(
          { longitude: lng, latitude: lat },
          selectedModePointsDraft,
          afterPointOrder,
        );
        const nextSegmentRatio = Number.isFinite(providedSegmentRatio)
          ? providedSegmentRatio
          : fallbackSegmentRatio;
        return {
          ...point,
          longitude: lng,
          latitude: lat,
          pointId: String(
            point?.pointId ?? lngLat?.pointId ?? lngLat?.point_id ?? "",
          ),
          labelCode: String(
            point?.labelCode ?? lngLat?.labelCode ?? lngLat?.label_code ?? "",
          ),
          afterPointOrder,
          segmentRatio: clampRouteShapeSegmentRatioInCurrentOrder(
            current,
            point,
            nextSegmentRatio,
            afterPointOrder,
          ),
        };
      });
      return {
        ...prev,
        [shapeKey]: normalizeRouteShapePointOrders(nextPoints),
      };
    });
    invalidateActiveWalkingRouteShape();
  }

  function handleDeleteWalkingRouteShapePoint(shapePointId) {
    if (selectedMode !== "walking" && selectedMode !== "driving") return;
    if (selectedMode === "walking" && !selectedWalkingRouteKey) return;
    if (!shapePointId) return;
    const shapeKey = getActiveRouteShapeKey(
      selectedMode,
      selectedWalkingRouteKey,
    );
    setWalkingRouteShapePointsByKey((prev) => ({
      ...prev,
      [shapeKey]: normalizeRouteShapePointOrders(
        (Array.isArray(prev?.[shapeKey]) ? prev[shapeKey] : []).filter(
          (point) => String(point?.id || "") !== String(shapePointId),
        ),
      ),
    }));
    setIsWalkingRouteShapeEditEnabled(false);
    invalidateActiveWalkingRouteShape();
  }

  function handleToggleWalkingRouteShapeEdit() {
    if (selectedMode !== "walking" && selectedMode !== "driving") return;
    setIsWalkingRouteShapeEditEnabled((current) => !current);
  }

  function stopWalkingRouteShapeEdit() {
    setIsWalkingRouteShapeEditEnabled(false);
  }

  async function handleSaveModeRedux() {
    if (isAdminRole && selectedMode !== "random") return;
    if (hasModePointValidationErrors) return;
    stopWalkingRouteShapeEdit();
    if (
      selectedMode === "driving" &&
      !isCurrentRouteReady(
        selectedMode,
        selectedModePointsDraft,
        selectedWalkingRouteKey,
      )
    ) {
      return;
    }
    // Debug snapshot used by the "save mode points" button.
    const routePayload = {
      modeToolDraft,
      points: selectedModePointsDraft,
      directiosApiRoutePointRaw:
        selectedMode === "random" ? null : directiosApiRoutePointRaw,
    };
    const resolvedWalkingTourId =
      selectedMode === "walking" ? resolveTourId(selectedStartTour) : "";
    if (
      selectedMode === "walking" &&
      !String(selectedWalkingRouteKey || "").trim()
    )
      return;
    const isUpdateWalkingRoute =
      selectedMode === "walking" &&
      isPersistedWalkingRouteId(selectedWalkingRouteKey);
    const isCreateWalkingRoute =
      selectedMode === "walking" &&
      String(selectedWalkingRouteKey || "").startsWith("new-");
    const normalizedPointsForMode = (
      Array.isArray(selectedModePointsDraft) ? selectedModePointsDraft : []
    ).map((point, index) => {
      const {
        isPersisted: _isPersisted,
        internalId: _internalId,
        serverId: _serverId,
        uuid: _uuid,
        ...safePoint
      } = point || {};
      const normalizedPoint = {
        ...safePoint,
        name: normalizeModeLocalizedFieldForSave(point?.name, languages),
        description: normalizeModeLocalizedFieldForSave(
          point?.description,
          languages,
        ),
        more_info: normalizeModeLocalizedFieldForSave(
          point?.more_info,
          languages,
        ),
        audios: normalizePointAudiosForSave(point),
        routeGroup: buildPayloadRouteGroup(point, selectedMode, index),
        labelCode: buildPayloadLabelCode(point, selectedMode, index),
      };
      if (selectedMode === "random") return normalizedPoint;
      const { pointType, ...rest } = normalizedPoint || {};
      return rest;
    });
    const payload = {
      modeToolDraft: {
        selectedTourTypeId: modeToolDraft?.selectedTourTypeId || "",
        routeKey:
          selectedMode === "walking" ? selectedWalkingRouteKey : "default",
        ...(selectedMode === "walking" && resolvedWalkingTourId
          ? { tourId: resolvedWalkingTourId }
          : {}),
        ...(isUpdateWalkingRoute
          ? { selectedTourTypeId: selectedWalkingRouteKey }
          : {}),
      },
      points: normalizedPointsForMode,
      ...(selectedMode === "walking" || selectedMode === "driving"
        ? {
            ...(selectedMode === "walking"
              ? {
                  name: normalizeModeLocalizedFieldForSave(
                    activeWalkingRouteMetaByKey?.[selectedWalkingRouteKey]
                      ?.name,
                    languages,
                  ),
                }
              : {}),
            route_shape_points: getActiveRouteShapePoints(
              selectedMode,
              selectedWalkingRouteKey,
            ).map((point, index) => ({
              id: String(point?.id || ""),
              latitude: String(point?.latitude ?? point?.lat ?? ""),
              longitude: String(point?.longitude ?? point?.lng ?? ""),
              after_point_order: Number(point?.afterPointOrder || 0) || 0,
              point_id: String(point?.pointId ?? point?.point_id ?? ""),
              label_code: String(point?.labelCode ?? point?.label_code ?? ""),
              segment_ratio:
                Number(point?.segmentRatio ?? point?.segment_ratio) || 0,
              order: Number(point?.order || index + 1) || index + 1,
            })),
          }
        : {}),
      directiosApiRoutePointRaw:
        selectedMode === "random" ? null : directiosApiRoutePointRaw,
      ...(selectedMode === "walking" && resolvedWalkingTourId
        ? { tourId: resolvedWalkingTourId }
        : {}),
    };
    try {
      let body = {};
      if (selectedMode === "walking" && resolvedWalkingTourId) {
        const isUpdateWalking = isPersistedWalkingRouteId(
          selectedWalkingRouteKey,
        );
        const walkingPayload = {
          ...payload,
          tourId: resolvedWalkingTourId,
          ...(isUpdateWalking
            ? {
                startRouteTypeId: selectedWalkingRouteKey,
              }
            : {}),
        };
        let response = null;
        if (isUpdateWalking) {
          response = await updateWalkingTourPointsRequest(walkingPayload);
        } else if (isCreateWalkingRoute) {
          response = await createWalkingTourPointsRequest(walkingPayload);
        } else {
          return;
        }
        body = response?.body || {};
        const createdRouteTypeId = String(body?.startRouteTypeId || "");
        dispatch(
          setDirectiosApiRoutePointRaw(body?.directiosApiRoutePointRaw ?? null),
        );
        if (
          createdRouteTypeId &&
          createdRouteTypeId !== selectedWalkingRouteKey
        ) {
          selectWalkingRoute(createdRouteTypeId);
        }
        await loadModePointsFromApi(
          resolvedWalkingTourId,
          "walking",
          selectedStartPoint?.id || resolvedWalkingTourId,
          createdRouteTypeId || selectedWalkingRouteKey,
        );
      } else if (selectedMode === "random") {
        const randomTourId = resolveTourId(selectedStartTour);
        if (!randomTourId) return;
        const firstPointType = String(
          selectedModePointsDraft?.[0]?.pointType ||
            payload?.pointType ||
            "building",
        );
        const randomPayload = {
          ...payload,
          tourId: randomTourId,
          pointType: firstPointType,
          points: normalizedPointsForMode.map((point) => ({
            ...point,
            pointType: String(point?.pointType || firstPointType),
          })),
          ...(selectedRandomRouteTypeId
            ? {
                startRouteTypeId: selectedRandomRouteTypeId,
                modeToolDraft: {
                  ...(payload?.modeToolDraft || {}),
                  selectedTourTypeId: selectedRandomRouteTypeId,
                },
              }
            : {}),
        };
        const response = selectedRandomRouteTypeId
          ? await updateRandomTourPointsRequest(randomPayload)
          : await createRandomTourPointsRequest(randomPayload);
        body = response?.body || {};
        setSelectedRandomRouteTypeId(
          String(body?.startRouteTypeId || selectedRandomRouteTypeId || ""),
        );
        dispatch(
          setDirectiosApiRoutePointRaw(body?.directiosApiRoutePointRaw ?? null),
        );
        await loadModePointsFromApi(
          randomTourId,
          "random",
          selectedStartPoint?.id || randomTourId,
          "default",
        );
      } else {
        const hasPersistedPoints = Array.isArray(selectedModePointsDraft)
          ? selectedModePointsDraft.some(
              (point) =>
                Boolean(
                  point?.isPersisted || point?.internalId || point?.serverId,
                ) || !String(point?.id || "").startsWith("point-"),
            )
          : false;
        const response = hasPersistedPoints
          ? await updateDrivingTourPointsRequest(payload)
          : await createDrivingTourPointsRequest(payload);
        body = response?.body || {};
        dispatch(
          setDirectiosApiRoutePointRaw(body?.directiosApiRoutePointRaw ?? null),
        );
        if (selectedTourTypeId && selectedMode && selectedStartPoint?.id) {
          await loadModePointsFromApi(
            selectedTourTypeId,
            selectedMode,
            selectedStartPoint.id,
            selectedMode === "walking" ? selectedWalkingRouteKey : "default",
          );
        }
      }
    } catch (error) {}
  }

  async function handleDeleteWalkingRoute(routeKey) {
    const currentRouteKey = String(routeKey || "");
    if (!currentRouteKey) return;
    const shouldDelete = window.confirm(
      "Are you sure you want to delete this walking route?",
    );
    if (!shouldDelete) return;
    const walkingTourId = resolveTourId(selectedStartTour);
    if (!walkingTourId || !selectedStartPoint?.id) return;
    if (currentRouteKey.startsWith("new-")) {
      removeWalkingRouteLocal(currentRouteKey);
      setWalkingRouteShapePointsByKey((prev) => {
        const next = { ...(prev || {}) };
        delete next[getActiveRouteShapeKey("walking", currentRouteKey)];
        return next;
      });
      return;
    }
    try {
      await deleteWalkingRouteRequest(currentRouteKey);
      setWalkingRouteShapePointsByKey((prev) => {
        const next = { ...(prev || {}) };
        delete next[getActiveRouteShapeKey("walking", currentRouteKey)];
        return next;
      });
      dispatch(resetDirectiosApiRoutePoint());
      dispatch(resetDirectiosApiRoutePointRaw());
      forceDrawExternalRoute(null);
      drawExternalRouteWithRetry(null);
      clearRouteGeometry();
      await loadModePointsFromApi(
        walkingTourId,
        "walking",
        selectedStartPoint.id,
        "",
      );
    } catch (error) {}
  }

  function handlePreviewAllTourRoutes({ routes = [], startPoints = [] } = {}) {
    drawCombinedRoutesPreview({ routes, startPoints, syncDraftPoints: false });
  }

  function handleClearPreviewRoutesOnMap() {
    drawCombinedRoutesPreview({
      routes: [],
      startPoints: [],
      syncDraftPoints: false,
    });
  }

  useEffect(() => {
    if (isPointMode) return;
    handleClearPreviewRoutesOnMap();
  }, [isPointMode]);

  function resolveMapPreviewTourId(tourOrTourId) {
    if (typeof tourOrTourId === "string" || typeof tourOrTourId === "number") {
      return String(tourOrTourId || "").trim();
    }
    return resolveTourId(tourOrTourId);
  }

  function normalizeModeLocalizedFieldForSave(value, languageItems = []) {
    const languageIsos = (Array.isArray(languageItems) ? languageItems : [])
      .map((language) =>
        String(language?.lng_iso || language?.iso || "")
          .toLowerCase()
          .trim(),
      )
      .filter(Boolean);

    const valueByIso = new Map();
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (!item || typeof item !== "object") return;
        const entry = Object.entries(item).find(([key]) => key !== "id");
        if (!entry) return;
        const [iso, localizedValue] = entry;
        const normalizedIso = String(iso || "")
          .toLowerCase()
          .trim();
        if (!normalizedIso) return;
        valueByIso.set(normalizedIso, {
          id: String(item?.id || ""),
          value: String(localizedValue || ""),
        });
      });
    } else if (value && typeof value === "object") {
      Object.entries(value).forEach(([iso, entry]) => {
        const normalizedIso = String(iso || "")
          .toLowerCase()
          .trim();
        if (!normalizedIso) return;
        if (entry && typeof entry === "object") {
          valueByIso.set(normalizedIso, {
            id: String(entry?.id || ""),
            value: String(entry?.value || ""),
          });
          return;
        }
        valueByIso.set(normalizedIso, {
          id: "",
          value: String(entry || ""),
        });
      });
    }

    return languageIsos.map((iso) => {
      const current = valueByIso.get(iso) || { id: "", value: "" };
      const normalizedValue = String(current.value || "");
      return {
        [iso]: normalizedValue,
        id: normalizedValue.trim() ? String(current.id || "") : "",
      };
    });
  }

  async function mapPreviewFetchAllTourPoints(
    event,
    tourOrTourId,
    routeType = "",
    routeId = "",
  ) {
    if (typeof event?.stopPropagation === "function") {
      event.stopPropagation();
    }
    const tourId = resolveMapPreviewTourId(tourOrTourId);
    if (!tourId) return;
    try {
      const response = await listAllTourPointsByTourRequest(tourId);
      const payload = response?.body || response || {};
      let routes = [];
      if (routeId) {
        routes = filterRoutesByTypeAndId(payload?.routes, routeType, routeId);
      } else if (routeType) {
        routes = filterRoutesByType(payload?.routes, routeType);
      } else {
        routes = Array.isArray(payload?.routes) ? payload.routes : [];
      }
      handlePreviewAllTourRoutes({
        routes,
        startPoints: Array.isArray(payload?.start_points)
          ? payload.start_points
          : [],
      });
    } catch (error) {}
  }

  function buildMapBoundaryPointsPayload(pointsList = []) {
    return (Array.isArray(pointsList) ? pointsList : [])
      .map((point, index) => {
        const lng = Number(point?.lng ?? point?.longitude);
        const lat = Number(point?.lat ?? point?.latitude);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
        return {
          order: index + 1,
          latitude: String(lat),
          longitude: String(lng),
        };
      })
      .filter(Boolean);
  }

  async function handleSaveMapBoundaries() {
    const tourId = resolveTourId(selectedMapBoundariesTour);
    const payload = {
      tourId,
      points: buildMapBoundaryPointsPayload(points),
    };
    if (!tourId) return;
    try {
      await syncTourBoundariesRequest(payload);
    } catch (error) {}
  }

  useEffect(() => {
    if (selectedMode !== "driving" && selectedMode !== "walking") return;
    if (!isModeToolOpen || !selectedStartPoint?.id) return;
    if (hydratingModeLoadRequestRef.current) return;
    const scopeKey = getActiveRouteScopeKey(
      selectedMode,
      selectedWalkingRouteKey,
    );
    const organized = Array.isArray(selectedModePointsDraft)
      ? selectedModePointsDraft
      : [];
    const sorted = [...organized].sort(
      (a, b) => Number(a?.number_point || 0) - Number(b?.number_point || 0),
    );
    const activeShapePoints =
      selectedMode === "walking" || selectedMode === "driving"
        ? getActiveRouteShapePoints(selectedMode, selectedWalkingRouteKey)
        : [];
    const coordinatesSignature =
      (selectedMode === "walking" || selectedMode === "driving") &&
      activeShapePoints.length
        ? buildWalkingRouteSignature(sorted, activeShapePoints)
        : buildModePointsSignature(sorted);
    const coordinates = sorted
      .map((item) => [Number(item?.longitude), Number(item?.latitude)])
      .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
    const shouldBlockAutoBuild = coordinates.length < 2;

    if (shouldBlockAutoBuild) {
      const blockedKey = `${scopeKey}::blocked::${coordinatesSignature}`;
      if (autoRouteBlockedRef.current === blockedKey) return;
      autoRouteBlockedRef.current = blockedKey;
      autoRouteRequestRef.current = blockedKey;
      dispatch(resetDirectiosApiRoutePoint());
      dispatch(resetDirectiosApiRoutePointRaw());
      forceDrawExternalRoute(null);
      drawExternalRouteWithRetry(null);
      clearRouteGeometry();
      setRouteReadySignatureByRoute((prev) => {
        if ((prev?.[scopeKey] || "") === "") return prev;
        return {
          ...prev,
          [scopeKey]: "",
        };
      });
      return;
    }

    autoRouteBlockedRef.current = "";
    const readySignature = routeReadySignatureByRoute?.[scopeKey] || "";
    const hydratedWithoutRouteSignature =
      hydratedWithoutRouteSignatureByScopeRef.current?.[scopeKey] || "";
    const requestKey = `${scopeKey}::${coordinatesSignature}`;
    if (
      !readySignature &&
      hydratedWithoutRouteSignature &&
      hydratedWithoutRouteSignature === coordinatesSignature
    ) {
      autoRouteRequestRef.current = requestKey;
      return;
    }
    if (readySignature === coordinatesSignature) {
      autoRouteRequestRef.current = requestKey;
      return;
    }
    if (autoRouteRequestRef.current === requestKey) return;

    autoRouteRequestRef.current = requestKey;
    handleBuildDrivingRoute();
  }, [
    clearRouteGeometry,
    dispatch,
    drawExternalRouteWithRetry,
    forceDrawExternalRoute,
    routeReadySignatureByRoute,
    isModeToolOpen,
    selectedMode,
    selectedModePointsDraft,
    selectedStartPoint?.id,
    selectedWalkingRouteKey,
    walkingRouteShapePointsByKey,
  ]);

  function unmountMapBoundariesFromMap() {
    clearBoundaryPolygon();
    clearAllPoints();
    forceDrawExternalRoute(null);
    drawExternalRouteWithRetry(null);
    clearRouteGeometry();
    handleClearPreviewRoutesOnMap();
  }

  useEffect(() => {
    // Loads persisted mode points/route when entering mode tool context.
    if (!isModeToolOpen || !selectedStartPoint?.id) return;
    const modeForLoad = selectedMode || pendingModeType || "driving";
    if (!modeForLoad) return;
    if (
      modeForLoad === "walking" &&
      String(selectedWalkingRouteKey || "").startsWith("new-")
    ) {
      // Local unsaved walking route: do not reload from backend or it will disappear.
      return;
    }
    if (
      modeForLoad === "driving" &&
      String(modeToolDraft?.selectedModeContext?.ownerId || "") !==
        String(selectedStartPoint.id || "")
    ) {
      // Wait until the new driving context is committed; otherwise a stale routeTypeId
      // from another start point can briefly load the wrong route.
      return;
    }
    const sourceIdForLoad =
      modeForLoad === "walking"
        ? resolveTourId(selectedStartTour)
        : selectedTourTypeId;
    if (!sourceIdForLoad) return;
    const routeKeyForLoad =
      modeForLoad === "walking" ? selectedWalkingRouteKey : "default";
    const contextKey = `${sourceIdForLoad}::${modeForLoad}::${selectedStartPoint.id}::${routeKeyForLoad}`;
    if (loadedModeContextRef.current === contextKey) return;
    loadedModeContextRef.current = contextKey;
    loadModePointsFromApi(
      sourceIdForLoad,
      modeForLoad,
      selectedStartPoint.id,
      routeKeyForLoad,
    ).catch((error) => {
      loadedModeContextRef.current = "";
    });
  }, [
    isModeToolOpen,
    modeToolDraft?.selectedModeContext?.ownerId,
    pendingModeType,
    selectedMode,
    selectedWalkingRouteKey,
    selectedStartPoint,
    selectedStartTour,
    selectedTourTypeId,
    walkingRouteShapePointsByKey,
  ]);

  useEffect(() => {
    // Any point structure change invalidates previously calculated route for save-step flow.
    const currentSignature =
      (selectedMode === "walking" || selectedMode === "driving") &&
      getActiveRouteShapePoints(selectedMode, selectedWalkingRouteKey).length
        ? buildWalkingRouteSignature(
            selectedModePointsDraft,
            getActiveRouteShapePoints(selectedMode, selectedWalkingRouteKey),
          )
        : buildModePointsSignature(selectedModePointsDraft);
    const scopeKey = getActiveRouteScopeKey();
    const activeReadySignature = routeReadySignatureByRoute?.[scopeKey] || "";
    if (activeReadySignature && currentSignature !== activeReadySignature) {
      setRouteReadySignatureByRoute((prev) => {
        if ((prev?.[scopeKey] || "") === "") return prev;
        return { ...prev, [scopeKey]: "" };
      });
    }
  }, [
    routeReadySignatureByRoute,
    selectedModePointsDraft,
    selectedMode,
    selectedWalkingRouteKey,
    walkingRouteShapePointsByKey,
  ]);

  useEffect(() => {
    setSelectedRandomRouteTypeId("");
  }, [selectedMode, selectedStartPoint?.id]);

  useEffect(() => {
    if (
      (selectedMode === "walking" || selectedMode === "driving") &&
      isModeToolOpen
    )
      return;
    setIsWalkingRouteShapeEditEnabled(false);
  }, [isModeToolOpen, selectedMode]);

  useEffect(() => {
    setIsWalkingRouteShapeEditEnabled(false);
  }, [selectedWalkingRouteKey]);

  useEffect(() => {
    if (!isModeToolOpen) return;
    loadedModeContextRef.current = "";
  }, [isModeToolOpen]);

  useEffect(() => {
    if (!isModeToolOpen || selectedMode !== "walking") {
      previousWalkingRouteKeyRef.current = "";
    }
  }, [isModeToolOpen, selectedMode, selectedStartPoint?.id]);

  useEffect(() => {
    if (
      selectedMode !== "walking" ||
      !isModeToolOpen ||
      !selectedStartPoint?.id
    )
      return;
    const currentRouteKey = String(selectedWalkingRouteKey || "");
    const previousRouteKey = String(previousWalkingRouteKeyRef.current || "");
    previousWalkingRouteKeyRef.current = currentRouteKey;
    if (!previousRouteKey || previousRouteKey === currentRouteKey) return;
    autoRouteRequestRef.current = "";
    autoRouteBlockedRef.current = "";
    dispatch(resetDirectiosApiRoutePoint());
    dispatch(resetDirectiosApiRoutePointRaw());
    forceDrawExternalRoute(null);
    drawExternalRouteWithRetry(null);
    clearRouteGeometry();
  }, [
    dispatch,
    drawExternalRouteWithRetry,
    forceDrawExternalRoute,
    clearRouteGeometry,
    isModeToolOpen,
    selectedMode,
    selectedStartPoint?.id,
    selectedWalkingRouteKey,
  ]);

  useEffect(() => {
    if (!selectedStartPoint || !selectedMode) return;
    setIsPickingStartPoints(false);
    setIsPickingModePoints(false);
  }, [selectedMode, selectedStartPoint]);

  useEffect(() => {
    if (selectedMode === "walking") {
      setRouteColor("#588354");
      setRouteDashArray([2, 2]);
      return;
    }
    if (selectedMode === "driving") {
      setRouteColor("#4faede");
      setRouteDashArray([1, 0]);
      return;
    }
  }, [selectedMode, setRouteColor, setRouteDashArray]);

  useEffect(() => {
    if (!pendingDrivingPointId || !selectedStartPoint?.id) return;
    if (String(pendingDrivingPointId) !== String(selectedStartPoint.id)) return;
    handleModeSelect(pendingModeType || "driving");
    setIsPickingModePoints(false);
    setPendingModeType("driving");
    setPendingDrivingPointId("");
  }, [
    handleModeSelect,
    pendingDrivingPointId,
    pendingModeType,
    selectedStartPoint,
  ]);

  function handleExitStartPointMode() {
    loadedModeContextRef.current = "";
    closeActivePointPopup();
    setIsPointMode(false);
    setSelectedStartTour(null);
    setSelectedStartPoint(null);
    setIsModeToolOpen(false);
    setAutoExpandStartPointId("");
    setIsPickingStartPoints(false);
    setIsPickingModePoints(false);
    resetToolState();
    dispatch(resetDirectiosApiRoutePoint());
    dispatch(resetDirectiosApiRoutePointRaw());
    clearRouteGeometry();
    handleClearPreviewRoutesOnMap();
    setStartPointTourId("");
    setStartPoints([]);
    clearAllPoints();
    clearBoundaryPolygon();
    closeMapBoundariesState();
    setRouteReadySignatureByRoute({});
  }

  function handleCloseBeforeYouGo() {
    setIsBeforeYouGoOpen(false);
    setSelectedBeforeYouGoTour(null);
    setBeforeYouGoSections([]);
    setLoadingBeforeYouGo(false);
  }

  function handleCloseMaximizeExperience() {
    setIsMaximizeExperienceOpen(false);
    setSelectedMaximizeExperienceTour(null);
    setMaximizeExperienceSections([]);
    setLoadingMaximizeExperience(false);
  }

  function handleCloseReviews() {
    setIsReviewsOpen(false);
    setSelectedReviewsTour(null);
  }

  async function handleSaveBeforeYouGo(payload = {}) {
    const tourId = resolveTourId(payload?.tour || selectedBeforeYouGoTour);
    const activeSections = Array.isArray(payload?.sections)
      ? payload.sections
      : [];
    const buildLocalizedPayload = (values = {}, ids = {}) =>
      Object.entries(values || {}).reduce((acc, [iso, value]) => {
        const normalizedValue = String(value ?? "");
        acc[iso] = {
          id: normalizedValue ? String(ids?.[iso] || "").trim() : "",
          value: normalizedValue,
        };
        return acc;
      }, {});
    const requestBody = {
      tourId,
      beforeYouGo: activeSections.map((section) => ({
        id: section?.id || "",
        itemType: section?.itemType || "category",
        order: Number(section?.order || 0) || 0,
        title: buildLocalizedPayload(
          section?.title || {},
          section?.titleTranslationIds || {},
        ),
        iconName: section?.iconName || "star",
        imageUrl: section?.imageUrl || "",
        image: section?.image || null,
        descriptions: buildLocalizedPayload(
          section?.descriptions || {},
          section?.descriptionTranslationIds || {},
        ),
        items: (Array.isArray(section?.items) ? section.items : []).map(
          (item) => ({
            id: item?.id || "",
            itemType: item?.itemType || "hotel",
            order: Number(item?.order || 0) || 0,
            displayOrder: Number(item?.displayOrder || 0) || 0,
            title: buildLocalizedPayload(
              item?.title || {},
              item?.titleTranslationIds || {},
            ),
            location: item?.location || "",
            description: buildLocalizedPayload(
              item?.description || {},
              item?.descriptionTranslationIds || {},
            ),
            rating: item?.rating ?? "",
            websiteUrl: item?.websiteUrl || "",
            imageUrl: item?.imageUrl || "",
            image: item?.image || null,
          }),
        ),
      })),
    };
    const response = await createBeforeYouGoRequest(requestBody);
    return response;
  }

  async function handleSaveMaximizeExperience(payload = {}) {
    const tourId = resolveTourId(
      payload?.tour || selectedMaximizeExperienceTour,
    );
    const activeSections = Array.isArray(payload?.sections)
      ? payload.sections
      : [];
    const buildLocalizedPayload = (values = {}, ids = {}) =>
      Object.entries(values || {}).reduce((acc, [iso, value]) => {
        const normalizedValue = String(value ?? "");
        acc[iso] = {
          id: normalizedValue ? String(ids?.[iso] || "").trim() : "",
          value: normalizedValue,
        };
        return acc;
      }, {});
    const requestBody = {
      tourId,
      maximazeYourExperience: activeSections.map((section) => ({
        id: section?.id || "",
        itemType: section?.itemType || "category",
        order: Number(section?.order || 0) || 0,
        title: buildLocalizedPayload(
          section?.title || {},
          section?.titleTranslationIds || {},
        ),
        iconName: section?.iconName || "star",
        imageUrl: section?.imageUrl || "",
        image: section?.image || null,
        descriptions: buildLocalizedPayload(
          section?.descriptions || {},
          section?.descriptionTranslationIds || {},
        ),
        items: (Array.isArray(section?.items) ? section.items : []).map(
          (item) => ({
            id: item?.id || "",
            itemType: item?.itemType || "hotel",
            order: Number(item?.order || 0) || 0,
            displayOrder: Number(item?.displayOrder || 0) || 0,
            title: buildLocalizedPayload(
              item?.title || {},
              item?.titleTranslationIds || {},
            ),
            location: item?.location || "",
            description: buildLocalizedPayload(
              item?.description || {},
              item?.descriptionTranslationIds || {},
            ),
            rating: item?.rating ?? "",
            websiteUrl: item?.websiteUrl || "",
            imageUrl: item?.imageUrl || "",
            image: item?.image || null,
          }),
        ),
      })),
    };
    console.log("[MaximizeExperience] requestBody", requestBody);
    const response = await syncMaximizeYourExperienceRequest(requestBody);
    console.log("[MaximizeExperience] response", response);
    return response;
  }

  function handleCloseMapBoundaries() {
    closeActivePointPopup();
    setIsPickingBoundaryPoints(false);
    setIsPickingStartPoints(false);
    setIsPickingModePoints(false);
    setIsDrawingEnabled(false);
    setIsPointMode(false);
    setIsModeToolOpen(false);
    setSelectedStartPoint(null);
    setSelectedStartTour(null);
    clearLocationMarker();
    previousPointsCountRef.current = 0;
    resetToolState();
    unmountMapBoundariesFromMap();
    closeMapBoundariesState();
  }

  function handleFocusStartPoint(point) {
    const lng = Number(point?.longitude ?? point?.lng);
    const lat = Number(point?.latitude ?? point?.lat);
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
    // Focus camera only; keep custom start-point marker visible without overlay marker.
    focusLocation({ lng, lat, zoom: 12.5, withMarker: false });
  }

  useEffect(() => {
    // Keeps start-point coordinates in sync after dragging markers,
    // even when "select new driving tour points" is not active.
    if (!isPointMode || isModeToolOpen || isMapBoundariesOpen) return;
    setStartPoints((current) => syncStartPointModelsFromMap(current, points));
  }, [isMapBoundariesOpen, isModeToolOpen, isPointMode, points]);

  useEffect(() => {
    if (!isPointMode || !isPickingStartPoints) return;
    const currentCount = Array.isArray(startPoints) ? startPoints.length : 0;
    if (currentCount <= previousPointsCountRef.current) {
      previousPointsCountRef.current = currentCount;
      return;
    }
    if (!startPoints?.length) return;
    const latestPoint = startPoints[startPoints.length - 1];
    if (!latestPoint?.id) return;
    setAutoExpandStartPointId(latestPoint.id);
    setIsPickingStartPoints(false);
    previousPointsCountRef.current = currentCount;
  }, [isPickingStartPoints, isPointMode, startPoints]);

  useEffect(() => {
    if (!isMapBoundariesOpen) return;
    setIsPointMode(true);
    setIsPickingBoundaryPoints(true);
    setIsDrawingEnabled(true);
    drawBoundaryPolygonFromPoints(points || []);
  }, [
    drawBoundaryPolygonFromPoints,
    isMapBoundariesOpen,
    points,
    setIsDrawingEnabled,
  ]);

  useEffect(() => {
    if (isMapBoundariesOpen) return;
    clearBoundaryPolygon();
  }, [clearBoundaryPolygon, isMapBoundariesOpen]);

  useEffect(() => {
    const isModeDrawingActive = Boolean(
      selectedStartPoint && selectedMode && isPickingModePoints,
    );
    if (isMapBoundariesOpen) {
      setIsDrawingEnabled(Boolean(isPointMode && isPickingBoundaryPoints));
      return;
    }
    setIsDrawingEnabled(
      Boolean(isPointMode && (isPickingStartPoints || isModeDrawingActive)),
    );
  }, [
    isMapBoundariesOpen,
    isPickingBoundaryPoints,
    isPickingModePoints,
    isPickingStartPoints,
    isPointMode,
    selectedMode,
    selectedStartPoint,
    setIsDrawingEnabled,
  ]);

  return (
    <CmsLayout activeModule="dashboard" title="Dashboard">
      <article className="panel dashboard-page-panel">
        {!process.env.REACT_APP_MAPBOX_TOKEN ? (
          <p className="message-error">
            Mapbox token is missing. Use `REACT_APP_MAPBOX_TOKEN`.
          </p>
        ) : null}
        <div className="dashboard-map-stage">
          <div
            ref={mapContainerRef}
            className="dashboard-map dashboard-map-full"
          />
          <MapStyleOverlayPanel
            selectedStyle={selectedStyle}
            onStyleChange={setSelectedStyle}
            is3DEnabled={is3DEnabled}
            onToggle3D={setIs3DEnabled}
            isMeasurementEnabled={isMeasurementEnabled}
            measurementDistanceKm={measurementDistanceKm}
            measurementPointCount={measurementPointCount}
            onToggleMeasurement={toggleMeasurement}
          />
          <MapOverlayPanel
              isHidden={
                isPointMode ||
                isBeforeYouGoOpen ||
                isMaximizeExperienceOpen ||
                isReviewsOpen
              }
              isTourFormOpen={isTourFormOpen}
              startPointMode={isPointMode}
              activeStartTour={selectedStartTour}
              onSelectTourForStartPoints={handleSelectTourForStartPoints}
              onSelectTourForWalkingPoints={handleSelectTourForWalkingPoints}
              onSelectTourForRandomPoints={handleSelectTourForRandomPoints}
              onSelectTourForMapBoundaries={handleSelectTourForMapBoundaries}
              onSelectTourForBeforeYouGo={handleSelectTourForBeforeYouGo}
              onSelectTourForMaximizeExperience={
                handleSelectTourForMaximizeExperience
              }
              onSelectTourForReviews={handleSelectTourForReviews}
              onOpenTourForm={() => setIsTourFormOpen(true)}
              isAdminReadOnly={isAdminRole}
              onBack={handleBackFromTourForm}
              languages={languages}
              languagesLoading={languagesLoading}
              languagesError={languagesError}
              onLocationPreview={focusLocation}
              onClearLocationPreview={clearLocationMarker}
              onPreviewAllTourRoutes={handlePreviewAllTourRoutes}
              onFetchAllTourPoints={mapPreviewFetchAllTourPoints}
            />
          {isBeforeYouGoOpen ? (
            <BeforeYouGoPanel
              activeTour={selectedBeforeYouGoTour}
              languages={languages}
              initialSections={beforeYouGoSections}
              loadingSections={loadingBeforeYouGo}
              panelTitle="Before you go"
              onClose={handleCloseBeforeYouGo}
              onSave={handleSaveBeforeYouGo}
            />
          ) : null}
          {isMaximizeExperienceOpen ? (
            <MaximizeYourExperiencePanel
              activeTour={selectedMaximizeExperienceTour}
              languages={languages}
              initialSections={maximizeExperienceSections}
              loadingSections={loadingMaximizeExperience}
              onClose={handleCloseMaximizeExperience}
              onSave={handleSaveMaximizeExperience}
            />
          ) : null}
          {isReviewsOpen ? (
            <ReviewsPanel
              activeTour={selectedReviewsTour}
              languages={languages}
              onClose={handleCloseReviews}
            />
          ) : null}
          {!isModeToolOpen && !isMapBoundariesOpen ? (
            <StartPointToolPanel
              startPointMode={isPointMode}
              activeStartTour={selectedStartTour}
              startTourId={startPointTourId}
              startPoints={startPoints}
              setStartPoints={setStartPoints}
              languages={languages}
              loadingStartPoints={loadingStartPoints}
              accordionResetKey={accordionResetKey}
              autoExpandPointId={autoExpandStartPointId}
              isPickingStartPoints={isPickingStartPoints}
              onTogglePickingStartPoints={handleTogglePickingStartPoints}
              onSelectStartPoint={handleSelectStartPoint}
              onAddDrivingModePoint={handleAddDrivingModePoint}
              onFetchAllTourPoints={mapPreviewFetchAllTourPoints}
              onClearRoutePreview={handleClearPreviewRoutesOnMap}
              onFocusStartPoint={handleFocusStartPoint}
              onRemoveStartPoint={removePointById}
              onRefreshStartPoints={() =>
                refreshStartPointsFromBackend(selectedStartTour)
              }
              onExitStartPointMode={handleExitStartPointMode}
            />
          ) : null}
          <MapBoundariesPanel
            isOpen={Boolean(isMapBoundariesOpen)}
            tourName={pickTourDisplayName(selectedMapBoundariesTour)}
            points={points}
            onAutoDrawBoundary={(currentPoints) =>
              drawBoundaryPolygonFromPoints(currentPoints || [])
            }
            onSave={handleSaveMapBoundaries}
            onRemovePoint={(pointId) => {
              if (!pointId) return;
              removePointById(pointId);
            }}
            onClose={handleCloseMapBoundaries}
          />
          <ModeRouteToolPanel
            startPointMode={
              isPointMode &&
              isModeToolOpen &&
              !isMapBoundariesOpen &&
              Boolean(selectedStartPoint)
            }
            selectedStartPoint={selectedStartPoint}
            modeOptions={modeOptions}
            selectedMode={selectedMode}
            isPickingModePoints={isPickingModePoints}
            modePointCount={modePointCount}
            modePoints={activeModePoints}
            selectedPointId={selectedModePointId}
            onSelectMode={handleModeSelect}
            onTogglePickingModePoints={handleTogglePickingModePoints}
            onSelectPoint={handleSelectModePoint}
            languages={languages}
            onCommitPoint={handleCommitModePoint}
            onUploadPointMedia={uploadModePointMediaFiles}
            onDeletePoint={handleDeleteModePoint}
            onSaveModeRedux={handleSaveModeRedux}
            canSaveModePoints={
              isAdminRole && selectedMode !== "random"
                ? false
                : selectedMode === "driving"
                  ? !hasModePointValidationErrors &&
                    isCurrentRouteReady(
                      selectedMode,
                      selectedModePointsDraft,
                      selectedWalkingRouteKey,
                    )
                  : !hasModePointValidationErrors
            }
            modeValidationHint={
              hasModePointValidationErrors
                ? selectedMode === "random"
                  ? "Complete required fields in each point: EN name, radius meters (> 0), case, point type, and at least 1 image or audio."
                  : "Complete required fields in each point: EN name, radius meters (> 0), case, and at least 1 image or audio."
                : ""
            }
            saveModePointsHint={
              isAdminRole && selectedMode !== "random"
                ? "Admin users can only save Points of Interest."
                : selectedMode === "driving"
                  ? "Route recalculates automatically. Save when it is ready."
                  : selectedMode === "walking"
                    ? "Route recalculates automatically. Save when it is ready."
                    : "Complete required fields and save mode points."
            }
            walkingRouteKeys={activeWalkingRouteKeys}
            selectedWalkingRouteKey={selectedWalkingRouteKey}
            walkingRouteMetaByKey={activeWalkingRouteMetaByKey}
            isWalkingRouteShapeEditEnabled={isWalkingRouteShapeEditEnabled}
            onSelectWalkingRoute={selectWalkingRoute}
            onAddWalkingRoute={createWalkingRoute}
            onChangeWalkingRouteName={updateWalkingRouteName}
            onToggleWalkingRouteShapeEdit={handleToggleWalkingRouteShapeEdit}
            onStopWalkingRouteShapeEdit={stopWalkingRouteShapeEdit}
            onDeleteWalkingRoute={handleDeleteWalkingRoute}
            onCloseTool={handleCloseModeTool}
            onActiveLanguageChange={setActiveModePopupLanguageIso}
            onImportJson={handleImportModeJson}
            isImportingJson={isImportingModeJson}
            onExportJson={handleExportModeJson}
            canExportJson={Boolean(
              (Array.isArray(selectedModePointsDraft) &&
                selectedModePointsDraft.length) ||
                (Array.isArray(activeModePoints) && activeModePoints.length),
            )}
            importJsonMessage={modeJsonImportMessage}
            importJsonError={modeJsonImportError}
          />
        </div>
      </article>
    </CmsLayout>
  );
}

export default DashboardPage;
